// src/pages/Dashboard.jsx
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useStore } from '../lib/store';
import {
  supabase, signOut, upsertProfile, uploadAvatar, uploadGalleryPhoto, getAllProfiles,
  getMessages, sendMessage, markMessageRead,
  getChatMessages, sendChatMessage, subscribeToChatRoom, getRoomId,
  getMyInvitations, createInvitation, respondToInvitation,
  getMyCalendar, addCalendarEntry, deleteCalendarEntry, updateCalendarEntry, createCampaign, getPublicEvents,
  createInviteToken, getAgentClients, getAgentTokens, getArtistProfile,
} from '../lib/supabase';
import { runAI, buildTourPlannerSystem } from '../lib/ai';
import { startSubscription, payForCampaign, checkSubscription, PRICES } from '../lib/stripe';
import Map, { Marker, Popup, NavigationControl } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { CalendarView } from '../components/CalendarView';
import FeaturedCarousel from '../components/FeaturedCarousel';
import { useT } from '../lib/i18n';

/* ── Design Tokens ── */
const C = {
  bg:"#13171d", bg2:"#1a1f27", card:"#1e2430", cardHov:"#242c3a",
  border:"#2c3545", borderHov:"#3a4555",
  orange:"#00c8b0", orangeLt:"#20e0c8", glow:"#40f0d8", amber:"#00a898",
  brown:"#006858", brownLt:"#008878",
  text:"#e8f0f8", muted:"#8a9ab0", dim:"#4a5a70", cream:"#c0d8f0",
  green:"#00e880", red:"#ff4860", blue:"#4090ff", purple:"#a060ff", tag:"#1e2d3d",
};

const typeColors = { artist: C.orange, venue: C.brown, fan: C.brownLt, booking_agent: '#f5c842' };
const typeLabels  = { artist: 'Artiste', venue: 'Lieu', fan: 'Fan', booking_agent: 'Agent de booking' };
const nowT = () => new Date().toLocaleTimeString('fr', { hour: '2-digit', minute: '2-digit' });

const REGION_COORDS = {
  'Montréal':{ lat:45.50, lng:-73.57 }, 'Québec':{ lat:46.82, lng:-71.22 },
  'Sherbrooke':{ lat:45.40, lng:-71.90 }, 'Toronto':{ lat:43.65, lng:-79.38 },
  'Vancouver':{ lat:49.28, lng:-123.12 }, 'Paris':{ lat:48.85, lng:2.35 },
  'Lyon':{ lat:45.75, lng:4.85 }, 'Bordeaux':{ lat:44.84, lng:-0.58 },
  'Marseille':{ lat:43.30, lng:5.37 }, 'Strasbourg':{ lat:48.57, lng:7.75 },
  'Metz':{ lat:49.12, lng:6.18 }, 'Bruxelles':{ lat:50.85, lng:4.35 },
  'Berlin':{ lat:52.52, lng:13.40 }, 'Amsterdam':{ lat:52.37, lng:4.90 },
  'Genève':{ lat:46.20, lng:6.15 }, 'Lausanne':{ lat:46.52, lng:6.63 },
};

const GENRES = ['Jazz / Soul','Rock / Indie','R&B / Neo Soul','Classique','Flamenco','Electronic / Techno','Folk / Chanson','Hip-Hop','Blues','Reggae / Dub','Musique du monde','Théâtre / Arts vivants','Danse','Variété','Multi-genres'];
const AVATARS = ['🎷','🎸','🎤','🎻','🥁','💃','🎹','🎺','🎭','🌟','🔥','🎪','🎵','🎼','🎬','🎨'];

const LEGAL_CLAUSES = [
  "L'artiste s'engage à se présenter à la date, heure et lieu convenus.",
  "Le lieu fournit les conditions techniques décrites dans le rider.",
  "Le cachet est réglé au plus tard 30 jours après la prestation.",
  "Annulation artiste <15 jours : 50% du cachet dû au lieu.",
  "Annulation lieu <15 jours : 100% du cachet dû à l'artiste.",
  "Les deux parties respectent les droits d'auteur (SACEM/SOCAN/CISAC).",
  "Les droits d'enregistrement sont négociés séparément.",
  "Toute modification doit être acceptée par écrit par les deux parties.",
];

const AD_PACKAGES = [
  { id:'local', name:'Local Spotlight', icon:'📍', price:29, color:C.amber, reach:'500–2 000 fans', features:['Bannière feed régional 7 jours','Notification push fans locaux','Badge Événement Local sur carte','Stats vues et clics'], platforms:['StageMap'], roi:'ROI estimé 3–5×' },
  { id:'boost', name:'Event Boost', icon:'🚀', price:89, color:C.orange, reach:'5 000–15 000', features:['Tout du pack Local','Meta Ads géociblés 50 km','Stories Instagram sponsorisée','Dashboard analytique'], platforms:['StageMap','Meta','Instagram'], roi:'ROI estimé 4–6×', badge:'Populaire' },
  { id:'pro', name:'Tour Pro', icon:'🌍', price:249, color:C.purple, reach:'20 000–80 000', features:['Tout du pack Boost','Google Ads CPC ~$5.26','TikTok Ads CPC $0.20','Spotify lien bio promo','Rapport par date'], platforms:['StageMap','Google','Meta','TikTok'], roi:'ROI estimé 5–8×' },
];

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

/* ── Mini components ── */
function Pill({ children, color = C.orange, sm }) {
  return <span style={{ background: color+'22', color, border:'1px solid '+color+'44', borderRadius:20, padding:sm?'1px 7px':'3px 10px', fontSize:sm?10:11, fontWeight:600, letterSpacing:.8, textTransform:'uppercase', fontFamily:"'Outfit',sans-serif", whiteSpace:'nowrap' }}>{children}</span>;
}
function Btn({ children, onClick, v='primary', sz='md', disabled, full, style:s={} }) {
  const base = { fontFamily:"'Outfit',sans-serif", fontWeight:600, cursor:disabled?'not-allowed':'pointer', border:'none', borderRadius:8, transition:'all .15s', opacity:disabled?.5:1, width:full?'100%':'auto', ...s };
  const sizes = { sm:{padding:'5px 12px',fontSize:12}, md:{padding:'9px 18px',fontSize:13}, lg:{padding:'13px 28px',fontSize:15} }[sz];
  const vars = {
    primary:   { background:'linear-gradient(135deg,'+C.orange+','+C.orangeLt+')', color:'#fff', boxShadow:'0 4px 16px '+C.orange+'44' },
    secondary: { background:C.tag, color:C.muted, border:'1px solid '+C.border },
    ghost:     { background:'none', color:C.muted, border:'1px solid '+C.border },
    danger:    { background:C.red+'22', color:C.red, border:'1px solid '+C.red+'44' },
    success:   { background:C.green+'22', color:C.green, border:'1px solid '+C.green+'44' },
    purple:    { background:'linear-gradient(135deg,'+C.purple+','+C.blue+')', color:'#fff' },
  }[v];
  return <button onClick={onClick} disabled={disabled} style={{...base,...sizes,...vars}}>{children}</button>;
}
function Inp({ value, onChange, placeholder, style:s={}, ml, rows=5 }) {
  const base = { background:C.tag, border:'1px solid '+C.border, borderRadius:8, padding:'8px 12px', color:C.text, fontFamily:"'Outfit',sans-serif", fontSize:13, outline:'none', width:'100%', ...s };
  return ml ? <textarea value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} rows={rows} style={{...base,resize:'vertical'}}/> : <input value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} style={base}/>;
}
function Sel({ value, onChange, options }) {
  return <select value={value} onChange={e=>onChange(e.target.value)} style={{ background:C.tag, border:'1px solid '+C.border, borderRadius:8, padding:'8px 12px', color:C.text, fontFamily:"'Outfit',sans-serif", fontSize:13, outline:'none', width:'100%' }}>{options.map(o=><option key={o} value={o}>{o}</option>)}</select>;
}
function Stars({ r }) {
  if (!r) return null;
  return <span style={{color:C.amber,fontSize:11}}>{'★'.repeat(Math.round(r))}{'☆'.repeat(5-Math.round(r))} {r.toFixed(1)}</span>;
}
function Spinner() {
  return <div style={{width:20,height:20,border:'2px solid '+C.border,borderTop:'2px solid '+C.orange,borderRadius:'50%',animation:'spin 1s linear infinite'}}/>;
}

/* ── Profile Switcher ── */
function ProfileSwitcher({ profile, userProfiles, onSwitch, onAdd, onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const t = useT();
  const tl = { artist: t('type.artist'), venue: t('type.venue'), fan: t('type.fan') };

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{ position:'relative', flexShrink:0 }}>
      <div onClick={() => setOpen(o => !o)}
        style={{ display:'flex', alignItems:'center', gap:6, background:C.tag, border:'1px solid '+(open?C.orange:C.border), borderRadius:20, padding:'4px 10px', cursor:'pointer', transition:'all .15s' }}>
        <span style={{ fontSize:16 }}>{profile.avatar || '🎵'}</span>
        <span style={{ fontSize:11, color:C.text, maxWidth:80, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{profile.name}</span>
        <span style={{ fontSize:9, color:C.dim }}>▾</span>
      </div>

      {open && (
        <div style={{ position:'absolute', top:'calc(100% + 8px)', right:0, background:C.bg2, border:'1px solid '+C.border, borderRadius:12, minWidth:210, zIndex:600, boxShadow:'0 16px 40px #00000090', overflow:'hidden' }}>
          <div style={{ padding:'8px 12px', borderBottom:'1px solid '+C.border, fontSize:10, color:C.dim, letterSpacing:1, textTransform:'uppercase' }}>{t('switcher.my_profiles')}</div>

          {userProfiles.map(p => (
            <div key={p.id} onClick={() => { onSwitch(p.id); setOpen(false); }}
              style={{ display:'flex', alignItems:'center', gap:9, padding:'9px 12px', cursor:'pointer', background: profile.id===p.id ? C.orange+'11' : 'none', borderLeft: profile.id===p.id ? '2px solid '+C.orange : '2px solid transparent', transition:'all .15s' }}
              onMouseEnter={e => e.currentTarget.style.background = C.card}
              onMouseLeave={e => e.currentTarget.style.background = profile.id===p.id ? C.orange+'11' : 'none'}>
              <span style={{ fontSize:18 }}>{p.avatar || '🎵'}</span>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:12, fontWeight: profile.id===p.id ? 600 : 400, color:C.text, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{p.name}</div>
                <div style={{ fontSize:10, color:C.muted }}>{tl[p.type]} · {p.region}</div>
              </div>
              {profile.id===p.id && <span style={{ fontSize:10, color:C.orange }}>✓</span>}
            </div>
          ))}

          <div style={{ borderTop:'1px solid '+C.border }}>
            <div onClick={() => { onAdd(); setOpen(false); }}
              style={{ display:'flex', alignItems:'center', gap:8, padding:'9px 12px', cursor:'pointer', color:C.orangeLt, fontSize:12 }}
              onMouseEnter={e => e.currentTarget.style.background = C.card}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}>
              {t('switcher.add')}
            </div>
            <div onClick={() => { onLogout(); setOpen(false); }}
              style={{ display:'flex', alignItems:'center', gap:8, padding:'9px 12px', cursor:'pointer', color:C.red, fontSize:12 }}
              onMouseEnter={e => e.currentTarget.style.background = C.card}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}>
              {t('switcher.logout')}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Map View (Mapbox) ── */
function MapView({ artists, myProfile, onOpen, onOpenEvent, events = [], allEvents = [] }) {
  const navigate = useNavigate();
  const [popup, setPopup] = useState(null);
  const [hov, setHov] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState(false);
  const t = useT();
  const tl = { artist: t('type.artist'), venue: t('type.venue'), fan: t('type.fan') };

  const withCoords = artists.filter(a => a.lat && a.lng);
  const centerLng = withCoords?.length ? withCoords.reduce((s,a)=>s+a.lng,0)/withCoords?.length : -45;
  const centerLat = withCoords?.length ? withCoords.reduce((s,a)=>s+a.lat,0)/withCoords?.length : 47;
  const [viewport, setViewport] = useState({ longitude: centerLng, latitude: centerLat, zoom: 3.2 });

  const tokenOk = MAPBOX_TOKEN && MAPBOX_TOKEN !== 'pk.eyJ1IjoieW91...' && MAPBOX_TOKEN.startsWith('pk.');
  if (!tokenOk || mapError) return <MapViewFallback artists={artists} myProfile={myProfile} onOpen={onOpen} />;

  return (
    <div>
      <div style={{ position:'relative', height:480, borderRadius:16, border:'1px solid '+C.border, overflow:'hidden', boxShadow:'0 8px 40px #00000060' }}>
        {!mapLoaded && (
          <div style={{ position:'absolute', inset:0, zIndex:10, background:'linear-gradient(135deg,#0a160a,#0a1220,#160a0a)', display:'flex', alignItems:'center', justifyContent:'center', gap:10 }}>
            <Spinner /><span style={{ color:C.muted, fontSize:13 }}>{t('loading')}</span>
          </div>
        )}
        <Map {...viewport} onMove={e=>setViewport(e.viewState)} mapboxAccessToken={MAPBOX_TOKEN} mapStyle="mapbox://styles/mapbox/dark-v11" style={{ width:'100%', height:'100%' }} onLoad={()=>setMapLoaded(true)} onError={()=>setMapError(true)} attributionControl={false}>
          <NavigationControl position="top-right" style={{ marginTop:8, marginRight:8 }} />
          {withCoords.map(a => {
            const isMe = myProfile && a.id === myProfile.id;
            const isH = hov === a.id;
            const size = isH ? 46 : isMe ? 40 : 34;
            const venueEvts = a.type === 'venue' ? allEvents.filter(ev => ev.user_id === a.id).slice(0, 6) : [];
            return (
              <Marker key={a.id} longitude={a.lng} latitude={a.lat} anchor="center" onClick={e=>{ e.originalEvent.stopPropagation(); setPopup(a); }}>
                <div onMouseEnter={()=>setHov(a.id)} onMouseLeave={()=>setHov(null)} style={{ position:'relative', cursor:'pointer', transition:'all .2s', zIndex:isH?20:isMe?15:10 }}>
                  <div style={{ position:'absolute', bottom:-4, left:'50%', transform:'translateX(-50%)', width:6, height:6, borderRadius:'50%', background:typeColors[a.type], boxShadow:'0 0 6px '+typeColors[a.type] }} />
                  <div style={{ width:size, height:size, borderRadius:'50%', background:C.card, border:isMe?'3px solid '+C.glow:'2px solid '+typeColors[a.type], display:'flex', alignItems:'center', justifyContent:'center', fontSize:isH?22:isMe?18:15, boxShadow:isH?'0 0 20px '+typeColors[a.type]+', 0 4px 12px #00000080':isMe?'0 0 14px '+C.glow+', 0 4px 12px #00000080':'0 2px 8px #00000060', transition:'all .2s' }}>
                    {a.avatar || '🎵'}
                  </div>
                  {isMe && !isH && <div style={{ position:'absolute', top:-4, right:-4, width:10, height:10, borderRadius:'50%', background:C.glow, border:'2px solid '+C.bg, animation:'pulse 2s infinite' }} />}
                  {venueEvts.length > 0 && !isH && <div style={{ position:'absolute', top:-3, right:-3, width:9, height:9, borderRadius:'50%', background:C.purple, border:'2px solid '+C.bg, boxShadow:'0 0 5px '+C.purple }} />}
                  {isH && !popup && (
                    <div style={{ position:'absolute', bottom:'calc(100% + 10px)', left:'50%', transform:'translateX(-50%)', background:C.bg2, border:'1px solid '+(isMe?C.glow:venueEvts.length>0?C.purple:C.border), borderRadius:10, padding:'8px 11px', color:C.text, fontSize:12, pointerEvents:'none', zIndex:30, boxShadow:'0 6px 24px #00000090', minWidth:150, maxWidth:230 }}>
                      {isMe && <div style={{ color:C.glow, fontSize:9, marginBottom:3 }}>{t('map.active')}</div>}
                      <strong style={{ whiteSpace:'nowrap' }}>{a.name}</strong>
                      <div style={{ color:C.muted, fontSize:10, whiteSpace:'nowrap', marginBottom: venueEvts.length > 0 ? 6 : 0 }}>{a.region}</div>
                      {venueEvts.length > 0 && <>
                        <div style={{ borderTop:'1px solid '+C.border, paddingTop:5, marginBottom:3 }}>
                          <div style={{ fontSize:9, color:C.dim, letterSpacing:1, textTransform:'uppercase', marginBottom:4 }}>{t('map.upcoming_events')}</div>
                          {venueEvts.map(ev=>(
                            <div key={ev.id} style={{ fontSize:10, color:C.purple, marginBottom:3, display:'flex', alignItems:'center', gap:3 }}>
                              <span style={{ flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{ev.title}</span>
                              <span style={{ color:C.dim, flexShrink:0, fontSize:9 }}>{new Date(ev.date_start).toLocaleDateString('fr',{day:'numeric',month:'short'})}</span>
                            </div>
                          ))}
                        </div>
                        <div style={{ fontSize:9, color:C.dim, textAlign:'center' }}>{t('map.click_profile')}</div>
                      </>}
                    </div>
                  )}
                </div>
              </Marker>
            );
          })}
          {events.filter(ev=>ev.profile?.lat&&ev.profile?.lng).map(ev=>(
            <Marker key={'ev_'+ev.id} longitude={ev.profile.lng} latitude={ev.profile.lat} anchor="center" onClick={e=>{ e.originalEvent.stopPropagation(); setPopup({...ev,_isEvent:true}); }}>
              <div style={{ cursor:'pointer', width:30, height:30, borderRadius:6, background:C.purple+'22', border:'2px solid '+C.purple, display:'flex', alignItems:'center', justifyContent:'center', fontSize:15, boxShadow:'0 0 12px '+C.purple+'66', transition:'all .2s' }}
                onMouseEnter={e=>{ e.currentTarget.style.transform='scale(1.2)'; }} onMouseLeave={e=>{ e.currentTarget.style.transform='none'; }}>
                📅
              </div>
            </Marker>
          ))}
          {popup && popup._isEvent ? (
            <Popup longitude={popup.profile.lng} latitude={popup.profile.lat} anchor="bottom" offset={28} onClose={()=>setPopup(null)} closeButton={false} style={{ zIndex:50 }}>
              <div style={{ background:C.bg2, border:'1px solid '+C.purple+'66', borderRadius:10, padding:'10px 13px', minWidth:190, fontFamily:"'Outfit',sans-serif", color:C.text }}>
                <div style={{ color:C.purple, fontSize:10, marginBottom:4 }}>{t('modal.public_event')}</div>
                <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:15, fontWeight:700, marginBottom:2 }}>{popup.title}</div>
                <div style={{ color:C.muted, fontSize:11, marginBottom:2 }}>{new Date(popup.date_start).toLocaleDateString('fr',{day:'numeric',month:'long',year:'numeric'})}</div>
                <div style={{ color:C.dim, fontSize:11, marginBottom:8 }}>{t('ev.by')} {popup.profile.name} · {popup.profile.region}</div>
                <div style={{ display:'flex', gap:6 }}>
                  <button onClick={()=>{ onOpenEvent(popup); setPopup(null); }} style={{ flex:1, background:'linear-gradient(135deg,'+C.purple+','+C.blue+')', border:'none', borderRadius:6, color:'#fff', fontSize:11, fontWeight:600, padding:'5px 0', cursor:'pointer', fontFamily:"'Outfit',sans-serif" }}>{t('modal.see_event')}</button>
                  <button onClick={()=>{ onOpen(popup.profile); setPopup(null); }} style={{ flex:1, background:C.tag, border:'1px solid '+C.purple+'55', borderRadius:6, color:C.purple, fontSize:11, fontWeight:600, padding:'5px 0', cursor:'pointer', fontFamily:"'Outfit',sans-serif" }}>{t('modal.see')}</button>
                </div>
              </div>
            </Popup>
          ) : popup && (
            <Popup longitude={popup.lng} latitude={popup.lat} anchor="bottom" offset={28} onClose={()=>setPopup(null)} closeButton={false} style={{ zIndex:50 }}>
              <div style={{ background:C.bg2, border:'1px solid '+(myProfile&&popup.id===myProfile.id?C.glow:C.border), borderRadius:10, padding:'10px 13px', minWidth:180, fontFamily:"'Outfit',sans-serif", color:C.text }}>
                {myProfile && popup.id === myProfile.id && <div style={{ color:C.glow, fontSize:10, marginBottom:4 }}>{t('modal.active')}</div>}
                <div style={{ display:'flex', gap:9, alignItems:'center', marginBottom:8 }}>
                  <span style={{ fontSize:26 }}>{popup.avatar || '🎵'}</span>
                  <div>
                    <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:15, fontWeight:700 }}>{popup.name}</div>
                    <div style={{ color:C.muted, fontSize:11 }}>{popup.genre}</div>
                    <div style={{ color:C.dim, fontSize:10 }}>📍 {popup.region}, {popup.country}</div>
                  </div>
                </div>
                {popup.available && <div style={{ display:'flex', alignItems:'center', gap:4, fontSize:10, color:C.green, marginBottom:7 }}><span style={{ width:5, height:5, borderRadius:'50%', background:C.green, display:'inline-block' }} />{t('status.available_booking')}</div>}
                {popup.type === 'venue' && allEvents.filter(ev=>ev.user_id===popup.id).length > 0 && (
                  <div style={{ marginBottom:8 }}>
                    <div style={{ fontSize:9, color:C.dim, letterSpacing:1, textTransform:'uppercase', marginBottom:5 }}>{t('map.upcoming_events')}</div>
                    {allEvents.filter(ev=>ev.user_id===popup.id).slice(0,8).map(ev=>(
                      <div key={ev.id} onClick={()=>{ onOpenEvent({...ev, profile: popup}); setPopup(null); }}
                        style={{ background:C.purple+'11', border:'1px solid '+C.purple+'33', borderRadius:5, padding:'3px 8px', fontSize:10, color:C.purple, marginBottom:3, display:'flex', justifyContent:'space-between', gap:6, cursor:'pointer' }}
                        onMouseEnter={e=>e.currentTarget.style.background=C.purple+'22'}
                        onMouseLeave={e=>e.currentTarget.style.background=C.purple+'11'}>
                        <span style={{ flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{ev.title}</span>
                        <span style={{ color:C.dim, flexShrink:0 }}>{new Date(ev.date_start).toLocaleDateString('fr',{day:'numeric',month:'short'})} →</span>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display:'flex', gap:6 }}>
                  <button onClick={()=>{ onOpen(popup); setPopup(null); }} style={{ flex:1, background:'linear-gradient(135deg,'+C.orange+','+C.orangeLt+')', border:'none', borderRadius:6, color:'#fff', fontSize:11, fontWeight:600, padding:'5px 0', cursor:'pointer', fontFamily:"'Outfit',sans-serif" }}>{t('modal.see')}</button>
                  <button onClick={()=>setPopup(null)} style={{ background:C.tag, border:'1px solid '+C.border, borderRadius:6, color:C.muted, fontSize:11, padding:'5px 8px', cursor:'pointer' }}>✕</button>
                </div>
              </div>
            </Popup>
          )}
        </Map>
        <div style={{ position:'absolute', bottom:12, left:12, background:C.card+'ee', border:'1px solid '+C.border, borderRadius:20, padding:'4px 11px', fontSize:11, color:C.muted, backdropFilter:'blur(8px)', pointerEvents:'none' }}>
          {t('map.profiles_count', {n: withCoords?.length})}
        </div>
      </div>
      <div style={{ display:'flex', gap:14, marginTop:8, justifyContent:'center', flexWrap:'wrap' }}>
        {events.length > 0
          ? <span style={{ display:'flex', alignItems:'center', gap:5, fontSize:11, color:C.muted }}><span style={{ width:10, height:10, borderRadius:3, background:C.purple, display:'inline-block' }} />{t('modal.public_event')}</span>
          : <>
            {Object.entries(typeColors).map(([type,c]) => (
              <span key={type} style={{ display:'flex', alignItems:'center', gap:5, fontSize:11, color:C.muted }}>
                <span style={{ width:8, height:8, borderRadius:'50%', background:c, display:'inline-block' }} />{tl[type]}
              </span>
            ))}
            <span style={{ display:'flex', alignItems:'center', gap:5, fontSize:11, color:C.muted }}>
              <span style={{ width:8, height:8, borderRadius:'50%', background:C.glow, display:'inline-block' }} />{t('map.you')}
            </span>
          </>
        }
      </div>
    </div>
  );
}

/* ── Map View Fallback ── */
function MapViewFallback({ artists, myProfile, onOpen }) {
  const [hov, setHov] = useState(null);
  const t = useT();
  const tl = { artist: t('type.artist'), venue: t('type.venue'), fan: t('type.fan') };
  return (
    <div>
      <div style={{position:'relative',height:460,background:'linear-gradient(135deg,#0a160a,#0a1220,#160a0a)',borderRadius:16,border:'1px solid '+C.border,overflow:'hidden'}}>
        {[...Array(10)].map((_,i)=><div key={i} style={{position:'absolute',left:(i*10)+'%',top:0,bottom:0,borderLeft:'1px solid '+C.border+'18'}}/>)}
        {[...Array(8)].map((_,i)=><div key={i} style={{position:'absolute',top:(i*12.5)+'%',left:0,right:0,borderTop:'1px solid '+C.border+'18'}}/>)}
        {[{l:'Québec',x:'17%',y:'11%'},{l:'France',x:'46%',y:'23%'},{l:'Belgique',x:'52%',y:'17%'},{l:'Allemagne',x:'60%',y:'13%'}].map(r=>(
          <div key={r.l} style={{position:'absolute',left:r.x,top:r.y,color:C.dim,fontSize:10,letterSpacing:2,textTransform:'uppercase',pointerEvents:'none'}}>{r.l}</div>
        ))}
        {artists.filter(a=>a.lat&&a.lng).map(a=>{
          const x=(a.lng+80)/200*100, y=(60-a.lat)/60*100, isH=hov===a.id, isMe=myProfile&&a.id===myProfile.id;
          return (
            <div key={a.id} onClick={()=>onOpen(a)} onMouseEnter={()=>setHov(a.id)} onMouseLeave={()=>setHov(null)}
              style={{position:'absolute',left:x+'%',top:y+'%',transform:'translate(-50%,-50%)',cursor:'pointer',zIndex:isH?20:isMe?15:10,transition:'all .2s'}}>
              <div style={{width:isH?44:isMe?38:32,height:isH?44:isMe?38:32,borderRadius:'50%',background:typeColors[a.type],border:isMe?'3px solid '+C.glow:'3px solid '+(isH?C.cream:C.bg),display:'flex',alignItems:'center',justifyContent:'center',fontSize:isH?20:isMe?17:13,boxShadow:isH?'0 0 24px '+typeColors[a.type]:isMe?'0 0 16px '+C.glow:'none',transition:'all .2s'}}>
                {a.avatar||'🎵'}
              </div>
              {isMe&&!isH&&<div style={{position:'absolute',top:-6,right:-4,width:10,height:10,borderRadius:'50%',background:C.glow,border:'2px solid '+C.bg}}/>}
              {isH&&<div style={{position:'absolute',bottom:'110%',left:'50%',transform:'translateX(-50%)',background:C.card,border:'1px solid '+(isMe?C.glow:C.border),borderRadius:8,padding:'6px 10px',whiteSpace:'nowrap',color:C.text,fontSize:12,pointerEvents:'none',zIndex:30}}>
                {isMe&&<span style={{color:C.glow,fontSize:10}}>✦ {t('map.you')} · </span>}{a.name}
                <div style={{color:C.muted,fontSize:10}}>{a.region}</div>
              </div>}
            </div>
          );
        })}
      </div>
      <div style={{display:'flex',gap:14,marginTop:8,justifyContent:'center'}}>
        {Object.entries(typeColors).map(([type,c])=><span key={type} style={{display:'flex',alignItems:'center',gap:5,fontSize:11,color:C.muted}}><span style={{width:8,height:8,borderRadius:'50%',background:c,display:'inline-block'}}/>{tl[type]}</span>)}
        <span style={{display:'flex',alignItems:'center',gap:5,fontSize:11,color:C.muted}}><span style={{width:8,height:8,borderRadius:'50%',background:C.glow,display:'inline-block'}}/>{t('map.you')}</span>
      </div>
    </div>
  );
}

/* ── Event Detail Modal (dashboard-internal) ── */
function EventModal({ ev, onClose }) {
  const t = useT();
  const p = ev.profile;
  const dateStr = new Date(ev.date_start).toLocaleDateString(t('cal.locale'), { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const timeStr = ev.time_start ? `${ev.time_start}${ev.time_end ? ' – ' + ev.time_end : ''}` : null;
  return (
    <div style={{ position:'fixed', inset:0, background:'#00000090', zIndex:1500, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{ background:C.bg2, border:'1px solid '+C.purple+'66', borderRadius:16, maxWidth:440, width:'100%', padding:26, boxShadow:'0 40px 100px #00000090', fontFamily:"'Outfit',sans-serif" }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:16 }}>
          <span style={{ background:C.purple+'22', color:C.purple, border:'1px solid '+C.purple+'44', borderRadius:20, padding:'2px 10px', fontSize:10, fontWeight:600 }}>{t('modal.public_event')}</span>
          <button onClick={onClose} style={{ background:'none', border:'none', color:C.dim, cursor:'pointer', fontSize:18 }}>×</button>
        </div>
        <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:22, fontWeight:700, color:C.cream, marginBottom:14, lineHeight:1.2 }}>{ev.title}</h2>
        <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:16 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, fontSize:13, color:C.text }}>
            <span>📅</span><span>{dateStr}</span>
          </div>
          {timeStr && <div style={{ display:'flex', alignItems:'center', gap:10, fontSize:13, color:C.muted }}><span>🕐</span><span>{timeStr}</span></div>}
          {ev.location && <div style={{ display:'flex', alignItems:'center', gap:10, fontSize:13, color:C.muted }}><span>📍</span><span>{ev.location}</span></div>}
          {p && <div style={{ display:'flex', alignItems:'center', gap:10, fontSize:13, color:C.muted }}><span>{p.avatar||'🎵'}</span><span>{p.name}{p.region ? ' · ' + p.region : ''}</span></div>}
        </div>
        {ev.description && <p style={{ fontSize:13, color:C.muted, lineHeight:1.6, marginBottom:16 }}>{ev.description}</p>}
        <div style={{ display:'flex', gap:8 }}>
          {p && <button onClick={()=>window.open('/profile/'+p.id,'_blank')} style={{ flex:1, background:'linear-gradient(135deg,'+C.purple+','+C.blue+')', border:'none', borderRadius:8, color:'#fff', fontSize:12, fontWeight:600, padding:'9px 0', cursor:'pointer', fontFamily:"'Outfit',sans-serif" }}>{t('modal.public')} →</button>}
          <button onClick={onClose} style={{ background:C.tag, border:'1px solid '+C.border, borderRadius:8, color:C.muted, fontSize:12, padding:'9px 16px', cursor:'pointer', fontFamily:"'Outfit',sans-serif" }}>{t('btn.close')}</button>
        </div>
      </div>
    </div>
  );
}

/* ── Profile Modal ── */
function ProfileModal({ a, myId, myProfile, onClose, onChat, onInvite }) {
  const isMe = a.id === myId;
  const canInvite = myProfile?.type === 'venue' ||
    (myProfile?.type === 'artist' && (myProfile?.artist_tier === 'local_legends' || myProfile?.artist_tier === 'all_stars'));
  const t = useT();
  const tl = { artist: t('type.artist'), venue: t('type.venue'), fan: t('type.fan') };
  return (
    <div style={{position:'fixed',inset:0,background:'#00000085',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{background:C.bg2,border:'1px solid '+(isMe?C.glow:C.border),borderRadius:16,padding:26,maxWidth:420,width:'100%',color:C.text,fontFamily:"'Outfit',sans-serif",boxShadow:'0 40px 100px #00000080'}}>
        {isMe&&<div style={{background:C.glow+'22',border:'1px solid '+C.glow+'44',borderRadius:8,padding:'5px 12px',fontSize:11,color:C.glow,marginBottom:14}}>{t('modal.active')}</div>}
        <div style={{display:'flex',gap:14,marginBottom:14}}>
          <div style={{fontSize:38,width:58,height:58,background:C.tag,borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',border:'2px solid '+typeColors[a.type]}}>{a.avatar||'🎵'}</div>
          <div>
            <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:19,fontWeight:700}}>{a.name}</div>
            <div style={{color:C.muted,fontSize:12,margin:'2px 0'}}>{a.genre} · {a.region}, {a.country}</div>
            <div style={{display:'flex',gap:6,flexWrap:'wrap',marginTop:4}}>
              <Pill color={typeColors[a.type]}>{tl[a.type]}</Pill>
              {a.available&&<span style={{color:C.green,fontSize:10,display:'flex',alignItems:'center',gap:3}}><span style={{width:5,height:5,borderRadius:'50%',background:C.green,display:'inline-block'}}/>{t('status.available')}</span>}
              {a.verified&&<span style={{color:C.blue,fontSize:10}}>{t('status.verified')}</span>}
            </div>
          </div>
        </div>
        {a.bio&&<p style={{color:C.muted,fontSize:13,lineHeight:1.6,marginBottom:10}}>{a.bio}</p>}
        {a.fee&&<div style={{color:C.amber,fontSize:12,marginBottom:8}}>💰 {a.fee}</div>}
        {a.rating>0&&<div style={{marginBottom:10}}><Stars r={a.rating}/> <span style={{color:C.dim,fontSize:11}}>({a.rating_count} avis)</span></div>}
        {a.links&&a.links?.length>0&&<div style={{marginBottom:14}}>{a.links.map((l,i)=><a key={i} href={'https://'+l} target='_blank' rel='noreferrer' style={{display:'inline-block',marginRight:4,marginBottom:4,background:C.tag,border:'1px solid '+C.border,color:C.orangeLt,borderRadius:5,padding:'2px 7px',fontSize:11,textDecoration:'none'}}>🔗 {l}</a>)}</div>}
        {/* Artist tier badge */}
        {a.artist_tier && a.artist_tier !== 'amateur' && (
          <div style={{marginBottom:10}}>
            <span style={{background:(a.artist_tier==='all_stars'?C.purple:C.orange)+'22',border:'1px solid '+(a.artist_tier==='all_stars'?C.purple:C.orange)+'44',color:a.artist_tier==='all_stars'?C.purple:C.orange,borderRadius:20,padding:'2px 10px',fontSize:10,fontWeight:700}}>
              {a.artist_tier==='all_stars'?'⭐ All Stars':'🎵 Local Legends'}
            </span>
          </div>
        )}
        {!isMe&&<div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          <Btn onClick={()=>{onChat(a);onClose();}}>💬 {t('modal.chat').replace('💬 ','')}</Btn>
          {canInvite && <Btn v="secondary" onClick={()=>{onInvite(a);onClose();}}>{t('modal.invite')}</Btn>}
          {a.type==='artist'&&<Btn v="purple" onClick={()=>{ window.open('/artist/'+a.id,'_blank'); }}>🎵 Page Artiste</Btn>}
          <Btn v="ghost" onClick={()=>{ window.open('/profile/'+a.id,'_blank'); }}>{t('modal.public')}</Btn>
          <Btn v="ghost" onClick={onClose}>✕</Btn>
        </div>}
        {isMe&&<div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          {a.type==='artist'&&<Btn v="purple" onClick={()=>{ window.open('/artist/'+a.id,'_blank'); }}>🎵 Ma Page Artiste</Btn>}
          <Btn v="ghost" onClick={()=>{ window.open('/profile/'+a.id,'_blank'); }} full>{t('modal.my_public')}</Btn>
          <Btn v="ghost" onClick={onClose}>{t('btn.close')}</Btn>
        </div>}
      </div>
    </div>
  );
}

/* ── Real-time Chat Panel ── */
function ChatPanel({ myProfile, partner, onClose }) {
  const [msgs, setMsgs] = useState([]);
  const [inp, setInp] = useState('');
  const [loading, setLoading] = useState(true);
  const endRef = useRef();
  const roomId = getRoomId(myProfile.id, partner.id);

  useEffect(() => {
    getChatMessages(roomId).then(data => { setMsgs(data); setLoading(false); });
    const sub = subscribeToChatRoom(roomId, payload => { setMsgs(m => [...m, payload.new]); });
    return () => supabase.removeChannel(sub);
  }, [roomId]);

  useEffect(() => { endRef.current&&endRef.current.scrollIntoView({behavior:'smooth'}); }, [msgs]);

  const send = async () => {
    if (!inp.trim()) return;
    const txt = inp; setInp('');
    await sendChatMessage(roomId, myProfile.id, txt);
  };

  return (
    <div style={{position:'fixed',right:24,bottom:24,width:330,height:440,background:C.bg2,border:'1px solid '+C.border,borderRadius:16,zIndex:900,display:'flex',flexDirection:'column',overflow:'hidden',boxShadow:'0 20px 60px #00000090'}}>
      <div style={{padding:'10px 14px',borderBottom:'1px solid '+C.border,display:'flex',alignItems:'center',gap:8,background:C.card}}>
        <span style={{fontSize:22}}>{partner.avatar||'🎵'}</span>
        <div style={{flex:1}}>
          <div style={{fontWeight:600,fontSize:13}}>{partner.name}</div>
          <div style={{display:'flex',alignItems:'center',gap:4}}><div style={{width:5,height:5,borderRadius:'50%',background:C.green}}/><span style={{fontSize:10,color:C.green}}>En ligne</span></div>
        </div>
        <button onClick={onClose} style={{background:'none',border:'none',color:C.dim,cursor:'pointer',fontSize:15}}>✕</button>
      </div>
      <div style={{flex:1,overflowY:'auto',padding:11,display:'flex',flexDirection:'column',gap:7}}>
        {loading&&<div style={{textAlign:'center',paddingTop:30}}><Spinner/></div>}
        {msgs.map(m=>{
          const isMe = m.sender_id===myProfile.id;
          return <div key={m.id} style={{display:'flex',justifyContent:isMe?'flex-end':'flex-start'}}>
            <div style={{background:isMe?C.orange:C.tag,color:isMe?'#fff':C.text,borderRadius:isMe?'11px 11px 2px 11px':'11px 11px 11px 2px',padding:'7px 10px',maxWidth:'80%',fontSize:12,lineHeight:1.5}}>
              {m.body}<div style={{fontSize:9,color:isMe?'#ffffff55':C.dim,marginTop:2,textAlign:'right'}}>{new Date(m.created_at).toLocaleTimeString('fr',{hour:'2-digit',minute:'2-digit'})}</div>
            </div>
          </div>;
        })}
        <div ref={endRef}/>
      </div>
      <div style={{padding:8,borderTop:'1px solid '+C.border,display:'flex',gap:6}}>
        <input value={inp} onChange={e=>setInp(e.target.value)} onKeyDown={e=>e.key==='Enter'&&send()} placeholder='Message...' style={{flex:1,background:C.tag,border:'1px solid '+C.border,borderRadius:7,padding:'6px 9px',color:C.text,fontFamily:"'Outfit',sans-serif",fontSize:12,outline:'none'}}/>
        <button onClick={send} style={{background:C.orange,border:'none',borderRadius:7,color:'#fff',padding:'6px 11px',cursor:'pointer',fontSize:13}}>➤</button>
      </div>
    </div>
  );
}

/* ── AI Tour Planner ── */
function AIPanel({ profiles = [], myProfile, onLaunchTour }) {
  const [mode, setMode] = useState('search');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [tourPlan, setTourPlan] = useState(null);
  const [err, setErr] = useState('');
  const t = useT();

  const run = async () => {
    if (!query.trim()) return;
    setLoading(true); setErr(''); setResult(null); setTourPlan(null);
    try {
      const system = buildTourPlannerSystem(profiles);
      const p = await runAI(system, query);
      if (p.mode==='tour') setTourPlan(p); else setResult(p);
    } catch(e) { setErr('Erreur IA : '+e.message); }
    setLoading(false);
  };

  const matched = result ? profiles.filter(p=>result.results&&result.results.includes(p.id)) : [];
  const SUGGESTIONS = {
    search: ['Jazz Montréal disponible','Salles France 200 places','Artistes électro Berlin','Flamenco budget 1000€'],
    tour:   ['Tournée jazz Canada→France 3 semaines','Circuit Europe flamenco 10 dates','Mini-tournée Québec 5 dates juin','Festival Belgique-France août'],
  };

  return (
    <div className='fade-in'>
      <div style={{marginBottom:16,display:'flex',gap:12,alignItems:'center'}}>
        <span style={{fontSize:28}}>🤖</span>
        <div>
          <h2 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:20,color:C.cream,fontWeight:700}}>{t('ai.title')}</h2>
          <p style={{color:C.muted,fontSize:12,marginTop:2}}>{t('ai.subtitle', {n: profiles?.length || 0})}</p>
        </div>
      </div>
      <div style={{display:'flex',gap:7,marginBottom:10}}>
        {[{k:'search',l:'🔍 '+t('ai.search_tab')},{k:'tour',l:'🗺️ '+t('ai.tour_tab')}].map(m=>(
          <button key={m.k} onClick={()=>setMode(m.k)} style={{background:mode===m.k?C.orange+'22':C.tag,color:mode===m.k?C.orange:C.muted,border:'1px solid '+(mode===m.k?C.orange:C.border),borderRadius:20,padding:'5px 14px',cursor:'pointer',fontSize:12,fontFamily:"'Outfit',sans-serif",fontWeight:mode===m.k?600:400}}>{m.l}</button>
        ))}
      </div>
      <div style={{display:'flex',gap:5,flexWrap:'wrap',marginBottom:9}}>
        {SUGGESTIONS[mode].map(s=><button key={s} onClick={()=>setQuery(s)} style={{background:C.tag,border:'1px solid '+C.border,color:C.muted,borderRadius:20,padding:'3px 9px',fontSize:11,cursor:'pointer',fontFamily:"'Outfit',sans-serif"}}>{s}</button>)}
      </div>
      <div style={{display:'flex',gap:9,marginBottom:14}}>
        <Inp value={query} onChange={setQuery} placeholder={mode==='search'?'Ex: saxophoniste jazz Québec budget 500$...':'Ex: tournée Montréal→Paris→Bruxelles 3 semaines...'} style={{flex:1}}/>
        <Btn onClick={run} disabled={loading||!query.trim()}>{loading?<Spinner/>:'✦ Générer'}</Btn>
      </div>
      {err&&<div style={{color:C.red,fontSize:13,padding:'8px 12px',background:C.red+'11',border:'1px solid '+C.red+'33',borderRadius:8,marginBottom:12}}>{err}</div>}
      {result&&<div className='fade-in'>
        <div style={{background:C.orange+'11',border:'1px solid '+C.orange+'33',borderRadius:10,padding:14,marginBottom:14}}>
          <div style={{color:C.orangeLt,fontWeight:600,marginBottom:3}}>✦ {result.summary}</div>
          {result.tip&&<div style={{color:C.muted,fontSize:12}}>💡 {result.tip}</div>}
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(230px,1fr))',gap:12}}>
          {matched.map(a=><ProfileCard key={a.id} a={a} myId={myProfile?.id} compact/>)}
          {matched?.length===0&&<div style={{color:C.dim,fontSize:13,gridColumn:'1/-1',textAlign:'center',padding:'20px 0'}}>{t('ai.no_match')}</div>}
        </div>
      </div>}
      {tourPlan&&<TourPlanCard plan={tourPlan} profiles={profiles} onLaunch={()=>onLaunchTour(tourPlan)}/>}
    </div>
  );
}

/* ── Tour Plan Card ── */
function TourPlanCard({ plan, profiles, onLaunch }) {
  const [exp, setExp] = useState(true);
  const t = useT();
  const getP = id => profiles.find(p=>p.id===id);
  return (
    <div className='fade-in' style={{background:C.card,border:'1px solid '+C.border,borderRadius:14,overflow:'hidden',marginTop:12}}>
      <div style={{background:'linear-gradient(135deg,'+C.bg2+','+C.card+')',padding:'12px 18px',display:'flex',alignItems:'center',gap:10}}>
        <span style={{fontSize:20}}>🗺️</span>
        <div style={{flex:1}}>
          <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:17,fontWeight:700,color:C.cream}}>{plan.title}</div>
          <div style={{color:C.muted,fontSize:11,marginTop:1}}>{plan.stops&&plan.stops?.length} étapes · {plan.totalDays} jours{plan.estimatedBudget&&' · '+plan.estimatedBudget}</div>
        </div>
        <button onClick={()=>setExp(e=>!e)} style={{background:'none',border:'none',color:C.muted,cursor:'pointer',fontSize:14}}>{exp?'▲':'▼'}</button>
      </div>
      {exp&&<div style={{padding:'14px 18px'}}>
        <div style={{color:C.muted,fontSize:13,marginBottom:12,lineHeight:1.6}}>{plan.summary}</div>
        <div style={{position:'relative',paddingLeft:20,marginBottom:14}}>
          <div style={{position:'absolute',left:6,top:0,bottom:0,width:2,background:'linear-gradient('+C.orange+','+C.brown+')'}}/>
          {plan.stops&&plan.stops.map((s,i)=>{
            const p=getP(s.profileId);
            return <div key={i} style={{position:'relative',marginBottom:10,paddingLeft:10}}>
              <div style={{position:'absolute',left:-18,top:4,width:8,height:8,borderRadius:'50%',background:i===0?C.glow:C.orange}}/>
              <div style={{background:C.tag,border:'1px solid '+C.border,borderRadius:8,padding:'8px 11px'}}>
                <div style={{display:'flex',alignItems:'center',gap:7,marginBottom:2}}>
                  <span style={{fontSize:14}}>{p?p.avatar:'📍'}</span>
                  <span style={{fontWeight:600,fontSize:12,color:C.text}}>{p?p.name:'Étape '+(i+1)}</span>
                  <Pill color={s.role==='headliner'?C.glow:C.brown} sm>{s.role}</Pill>
                  <span style={{marginLeft:'auto',color:C.orange,fontSize:11}}>{s.date}</span>
                </div>
                <div style={{color:C.muted,fontSize:11}}>📍 {s.city}{s.note?' — '+s.note:''}</div>
              </div>
            </div>;
          })}
        </div>
        {plan.legalNote&&<div style={{background:C.amber+'11',border:'1px solid '+C.amber+'33',borderRadius:8,padding:9,marginBottom:12,fontSize:12,color:C.amber}}>⚖️ {plan.legalNote}</div>}
        <Btn onClick={onLaunch}>{t('ai.launch')}</Btn>
      </div>}
    </div>
  );
}

/* ── Profile Card ── */
function ProfileCard({ a, myId, compact, onOpen }) {
  const [hov, setHov] = useState(false);
  const t = useT();
  const tl = { artist: t('type.artist'), venue: t('type.venue'), fan: t('type.fan') };
  return (
    <div onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)} onClick={()=>onOpen&&onOpen(a)}
      style={{background:hov?C.cardHov:C.card,border:'1px solid '+(hov?C.borderHov:C.border),borderRadius:12,padding:compact?14:20,cursor:onOpen?'pointer':'default',transition:'all .2s',transform:hov&&onOpen?'translateY(-2px)':'none'}}>
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:8}}>
        <div style={{fontSize:compact?28:36,width:compact?44:56,height:compact?44:56,background:C.tag,borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',border:'2px solid '+typeColors[a.type]+'44',flexShrink:0}}>{a.avatar||'🎵'}</div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:compact?15:17,fontWeight:700,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{a.name}</div>
          <div style={{display:'flex',gap:5,flexWrap:'wrap',marginTop:3}}>
            <Pill color={typeColors[a.type]} sm>{tl[a.type]}</Pill>
            {a.available&&<span style={{color:C.green,fontSize:10,display:'flex',alignItems:'center',gap:3}}><span style={{width:5,height:5,borderRadius:'50%',background:C.green,display:'inline-block'}}/>{t('status.dispo')}</span>}
            {myId&&a.id===myId&&<span style={{color:C.glow,fontSize:10}}>✦ {t('map.you')}</span>}
          </div>
        </div>
      </div>
      <div style={{color:C.muted,fontSize:11,marginBottom:4}}>📍 {a.region}, {a.country} · 🎼 {a.genre}</div>
      {!compact&&a.bio&&<div style={{color:C.dim,fontSize:12,lineHeight:1.5,marginBottom:6}}>{a.bio}</div>}
      {a.fee&&<div style={{color:C.amber,fontSize:11,marginTop:4}}>💰 {a.fee}</div>}
    </div>
  );
}

/* ── Invitation Modal ── */
function InviteModal({ organizer, invitee, profiles, onClose, onSent }) {
  const [form, setForm] = useState({ tour_title:'Invitation à participer', city:invitee.region||'', date:'', role:'headliner', note:'', cal_visibility:'private' });  const [sig, setSig] = useState('');
  const [legalOk, setLegalOk] = useState(false);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [warningAccepted, setWarningAccepted] = useState(false);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const isOrganizerAmateurArtist = organizer?.type === 'artist' && (!organizer?.artist_tier || organizer?.artist_tier === 'amateur');
  const isOrganizerAmateurVenue = organizer?.type === 'venue' && (!organizer?.venue_type || organizer?.venue_type === 'amateur');
  const inviteePro = invitee?.type === 'artist' && (invitee?.artist_tier === 'local_legends' || invitee?.artist_tier === 'all_stars');
  const needsWarning = isOrganizerAmateurVenue && inviteePro && !warningAccepted;

const send = async () => {
    setLoading(true);
    try {
      const inv = await createInvitation({ tour_title:form.tour_title||'Invitation à participer', organizer_id:organizer.id, invitee_id:invitee.id, city:form.city, date:form.date||null, role:form.role, note:form.note, status:'pending', legal_accepted_by_organizer:true, organizer_signature:sig, cal_visibility:form.cal_visibility });
await sendMessage(organizer.user_id || organizer.id, invitee.user_id || invitee.id, form.tour_title, (form.note ? form.note + '\n\n' : '') + '📅 Date : ' + (form.date || 'À confirmer') + '\n📍 Lieu : ' + (form.city || 'À confirmer') + '\n🎭 Rôle : ' + form.role, true, inv.id);      toast.success('Invitation envoyée à '+invitee.name+' !');
      onSent();
    } catch(e) { toast.error('Erreur: '+e.message); }
    setLoading(false);
  };
  return (
    <div style={{position:'fixed',inset:0,background:'#00000090',zIndex:2000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{background:C.bg2,border:'1px solid '+C.border,borderRadius:16,maxWidth:520,width:'100%',maxHeight:'90vh',overflow:'hidden',display:'flex',flexDirection:'column',boxShadow:'0 40px 100px #00000090'}}>
        <div style={{padding:'15px 22px',borderBottom:'1px solid '+C.border,background:C.card,display:'flex',alignItems:'center',gap:10}}>
          <span style={{fontSize:20}}>🗺️</span>
          <div style={{flex:1}}>
            <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:16,fontWeight:700,color:C.cream}}>Inviter à participer</div>
            <div style={{color:C.muted,fontSize:11}}>{invitee.avatar} {invitee.name}</div>
          </div>
          <button onClick={onClose} style={{background:'none',border:'none',color:C.dim,cursor:'pointer',fontSize:16}}>✕</button>
        </div>
        <div style={{flex:1,overflowY:'auto',padding:'16px 22px'}}>
          {isOrganizerAmateurArtist && (
            <div style={{textAlign:'center',padding:'30px 0'}}>
              <div style={{fontSize:36,marginBottom:12}}>🚫</div>
              <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:18,fontWeight:700,color:C.cream,marginBottom:8}}>Accès non disponible</div>
              <div style={{color:C.muted,fontSize:13,lineHeight:1.7,marginBottom:20}}>Les comptes <strong style={{color:C.text}}>Amateur</strong> ne peuvent pas envoyer d'invitations.<br/>Passez à <strong style={{color:C.orange}}>Local Legends</strong> ou <strong style={{color:C.purple}}>All Stars</strong> pour débloquer cette fonctionnalité.</div>
              <Btn v='secondary' onClick={onClose}>Fermer</Btn>
            </div>
          )}
          {!isOrganizerAmateurArtist && needsWarning && (
            <div style={{textAlign:'center',padding:'20px 0'}}>
              <div style={{fontSize:36,marginBottom:12}}>⚠️</div>
              <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:18,fontWeight:700,color:C.amber,marginBottom:10}}>Artiste professionnel</div>
              <div style={{color:C.muted,fontSize:13,lineHeight:1.8,marginBottom:20,background:C.amber+'11',border:'1px solid '+C.amber+'33',borderRadius:10,padding:'14px 16px'}}>
                <strong style={{color:C.text}}>{invitee.name}</strong> est un·e artiste {invitee.artist_tier === 'all_stars' ? <strong style={{color:C.purple}}>All Stars</strong> : <strong style={{color:C.orange}}>Local Legends</strong>}.<br/><br/>
                Ces artistes exercent leur métier de façon professionnelle et sont <strong style={{color:C.text}}>généralement rémunérés</strong> pour leurs performances. En continuant, vous comprenez que cette invitation implique potentiellement une entente financière.
              </div>
              <div style={{display:'flex',gap:10,justifyContent:'center'}}>
                <Btn v='secondary' onClick={onClose}>Annuler</Btn>
                <Btn onClick={()=>setWarningAccepted(true)}>Je comprends, continuer →</Btn>
              </div>
            </div>
          )}
          {!isOrganizerAmateurArtist && !needsWarning && step===0&&<div style={{display:'flex',flexDirection:'column',gap:13}}>
            {[{l:'Titre de la tournée',k:'tour_title',p:'Ex: Tournée Jazz Automne 2026'},{l:'Ville / Date',k:'city',p:'Montréal, Paris...'},{l:'Message personnel',k:'note',p:'Détails, attentes, conditions...'}].map(f=>(
              <div key={f.k}>
                <div style={{fontSize:10,color:C.dim,letterSpacing:1,textTransform:'uppercase',marginBottom:5}}>{f.l}</div>
                <Inp value={form[f.k]} onChange={v=>set(f.k,v)} placeholder={f.p} ml={f.k==='note'} rows={2}/>
              </div>
            ))}
            <div>
              <div style={{fontSize:10,color:C.dim,letterSpacing:1,textTransform:'uppercase',marginBottom:5}}>Date proposée</div>
              <input type='date' value={form.date} onChange={e=>set('date',e.target.value)} style={{background:C.tag,border:'1px solid '+C.border,borderRadius:8,padding:'8px 12px',color:C.text,fontFamily:"'Outfit',sans-serif",fontSize:13,outline:'none',width:'100%'}}/>
            </div>
            <div>
              <div style={{fontSize:10,color:C.dim,letterSpacing:1,textTransform:'uppercase',marginBottom:5}}>Rôle</div>
              <Sel value={form.role} onChange={v=>set('role',v)} options={['headliner','support','venue','special']}/>
            </div>
            <Btn onClick={()=>setStep(1)}>Continuer → Accord légal</Btn>
          </div>}
          {!isOrganizerAmateurArtist && !needsWarning && step===1&&<div>
            <div style={{background:C.tag,border:'1px solid '+C.border,borderRadius:8,padding:12,marginBottom:12}}>
              <div style={{fontSize:10,letterSpacing:2,textTransform:'uppercase',color:C.orange,marginBottom:8,fontWeight:600}}>Contrat simplifié StageMap</div>
              {LEGAL_CLAUSES.map((c,i)=><div key={i} style={{display:'flex',gap:7,marginBottom:6,fontSize:11,color:C.muted,lineHeight:1.5}}><span style={{color:C.orange,fontWeight:700,flexShrink:0}}>{i+1}.</span><span>{c}</span></div>)}
            </div>
            <div style={{background:C.amber+'11',border:'1px solid '+C.amber+'33',borderRadius:6,padding:8,fontSize:11,color:C.amber,marginBottom:12}}>⚖️ Accord de principe. Un contrat complet peut être demandé avant confirmation finale.</div>
            <label style={{display:'flex',alignItems:'center',gap:8,marginBottom:12,cursor:'pointer'}}>
              <input type='checkbox' checked={legalOk} onChange={e=>setLegalOk(e.target.checked)} style={{accentColor:C.orange,width:14,height:14}}/>
              <span style={{fontSize:12,color:C.text}}>J'accepte les conditions en tant qu'organisateur</span>
            </label>
            <Inp value={sig} onChange={setSig} placeholder='Votre nom complet (signature électronique)...' style={{marginBottom:14}}/>
            <div style={{display:'flex',gap:9}}>
              <Btn v='ghost' onClick={()=>setStep(0)}>← Retour</Btn>
              <Btn onClick={send} disabled={!legalOk||!sig.trim()||loading}>{loading?<Spinner/>:"🚀 Envoyer l'invitation"}</Btn>
            </div>
          </div>}
        </div>
      </div>
    </div>
  );
}

/* ── Inbox ── */
function InboxView({ messages, myId, profileIds, profiles, onChat, onRefresh }) {
  const [sel, setSel] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [invLoading, setInvLoading] = useState({});
  const t = useT();

useEffect(() => { 
    if(myId) getMyInvitations(myId).then(data=>setInvitations(data||[])); 
  }, [myId]);
  const respond = async (invId, status) => {
    setInvLoading(l=>({...l,[invId]:true}));
    try {
      await respondToInvitation(invId, status);
      setInvitations(prev=>prev.map(i=>i.id===invId?{...i,status}:i));
toast.success(status==='accepted'?'Invitation acceptée ✓ — Vérifiez votre agenda !':'Invitation déclinée');
if (status === 'accepted') setTimeout(() => window.location.reload(), 1500);    } catch(e) { toast.error(e.message); }
    setInvLoading(l=>({...l,[invId]:false}));
  };

  const getProfile = (id) => profiles.find(p=>p.id===id||p.user_id===id)||{name:'Utilisateur',avatar:'👤'};  
  const pendingInvites = invitations.filter(i=>i.status==='pending');
  return (
    <div className='fade-in' style={{display:'grid',gridTemplateColumns:'270px 1fr',gap:14,minHeight:400}}>
      <div>
        {pendingInvites?.length>0&&(
          <div style={{background:C.orange+'11',border:'1px solid '+C.orange+'33',borderRadius:9,padding:10,marginBottom:10}}>
            <div style={{fontSize:11,color:C.orange,fontWeight:600,marginBottom:6}}>🗺️ {pendingInvites?.length} {t('inbox.pending')}</div>
            {pendingInvites.map(inv=>{
              const org=getProfile(inv.organizer_id);
              return <div key={inv.id} style={{background:C.card,border:'1px solid '+C.border,borderRadius:8,padding:10,marginBottom:6}}>
                <div style={{fontWeight:600,fontSize:12,marginBottom:3}}>{org.avatar} {org.name}</div>
                <div style={{color:C.muted,fontSize:11,marginBottom:6}}>📍 {inv.city} · {inv.role} · {inv.date}</div>
                <div style={{display:'flex',gap:6}}>
                  <Btn v='success' sz='sm' disabled={invLoading[inv.id]} onClick={()=>respond(inv.id,'accepted')}>{t('inbox.accept')}</Btn>
                  <Btn v='danger' sz='sm' disabled={invLoading[inv.id]} onClick={()=>respond(inv.id,'declined')}>{t('inbox.decline')}</Btn>
                  <Btn v='ghost' sz='sm' onClick={()=>onChat(getProfile(inv.organizer_id))}>💬</Btn>
                </div>
              </div>;
            })}
          </div>
        )}
        {messages.map(m=>{
const other=getProfile(m.from_id===myId?m.to_id:m.from_id);          return <div key={m.id} onClick={()=>{setSel(m);if(!m.read&&m.to_id===myId)markMessageRead(m.id);}}
            style={{background:sel?.id===m.id?C.cardHov:C.card,border:'1px solid '+(sel?.id===m.id?C.borderHov:C.border),borderLeft:'3px solid '+(m.read?C.border:C.orange),borderRadius:9,padding:11,marginBottom:6,cursor:'pointer',transition:'all .15s'}}>
            <div style={{display:'flex',gap:8,alignItems:'center'}}>
              <span style={{fontSize:18}}>{other.avatar||'👤'}</span>
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:1}}>
                  <span style={{fontWeight:m.read?400:700,fontSize:12}}>{other.name}</span>
                  <span style={{fontSize:10,color:C.dim}}>{new Date(m.created_at).toLocaleDateString('fr')}</span>
                </div>
                <div style={{fontSize:11,color:m.read?C.dim:C.muted,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{m.subject}</div>
              </div>
            </div>
          </div>;
        })}
        {messages?.length===0&&pendingInvites?.length===0&&<div style={{color:C.dim,fontSize:13,textAlign:'center',padding:'24px 0'}}>{t('inbox.empty')}</div>}
      </div>
      <div style={{background:C.card,border:'1px solid '+C.border,borderRadius:11,padding:20}}>
        {!sel?<div style={{color:C.dim,textAlign:'center',paddingTop:50,fontSize:13}}>{t('inbox.select')}</div>:(
          <div>
            <div style={{display:'flex',gap:10,alignItems:'center',marginBottom:14,paddingBottom:12,borderBottom:'1px solid '+C.border}}>
              <span style={{fontSize:26}}>{getProfile(sel.from_id===myId?sel.to_id:sel.from_id).avatar||'👤'}</span>
              <div>
                <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:17,fontWeight:700}}>{sel.subject}</div>
                <div style={{color:C.muted,fontSize:11}}>{t('inbox.from')} {getProfile(sel.from_id).name} · {new Date(sel.created_at).toLocaleDateString('fr')}</div>
              </div>
            </div>
            <div style={{color:C.muted,fontSize:13,lineHeight:1.7,marginBottom:14}}>{sel.body}</div>
            <Btn v='secondary' sz='sm' onClick={()=>{
              const otherId = sel.from_id===myId ? sel.to_id : sel.from_id;
              const p = profiles.find(pr => pr.id===otherId || pr.user_id===otherId);
              if(p) onChat(p);
              else toast.error('Profil introuvable');
            }}>{t('inbox.reply_chat')}</Btn>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Calendar View ── */


/* ── Client Profile Manager (agent editing a client's profile + agenda) ── */
const CAL_TYPE_COLORS = { availability: C.green, event: C.orange, booking: C.amber, personal: C.muted };
const CAL_TYPE_LABELS = { availability: 'Disponibilité', event: 'Événement', booking: 'Booking', personal: 'Personnel' };
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('fr-CA', { year:'numeric', month:'short', day:'numeric' }) : '—';

function ClientProfileManager({ clientId, agentProfile, allClients = [], onClose, onSaved }) {
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [view, setView] = useState('profile');
  const [calEntries, setCalEntries] = useState([]);
  const [calLoading, setCalLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newEntry, setNewEntry] = useState({ title:'', event_type:'booking', date_start:'', date_end:'', visibility:'private', counterpartId:'' });
  const [addingEntry, setAddingEntry] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const setField = (k, v) => setP(prev => ({ ...prev, [k]: v }));
  const setNE = (k, v) => setNewEntry(prev => ({ ...prev, [k]: v }));

  useEffect(() => {
    getArtistProfile(clientId).then(data => {
      setP({ ...data, links: Array.isArray(data.links) ? data.links.join(', ') : data.links || '' });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [clientId]);

  useEffect(() => {
    if (view !== 'calendar') return;
    setCalLoading(true);
    getMyCalendar(clientId).then(setCalEntries).catch(() => {}).finally(() => setCalLoading(false));
  }, [view, clientId]);

  const save = async () => {
    setSaving(true);
    try {
      await upsertProfile({ ...p, links: p.links ? p.links.split(',').map(l => l.trim()).filter(Boolean) : [] });
      toast.success('Profil client mis à jour !');
      onSaved();
    } catch (e) { toast.error('Erreur: ' + e.message); }
    setSaving(false);
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !p) return;
    setAvatarUploading(true);
    try {
      const url = await uploadAvatar(p.id, file);
      setField('avatar_url', url);
      await upsertProfile({ id: p.id, avatar_url: url });
      toast.success('Photo mise à jour !');
    } catch (err) { toast.error('Erreur: ' + err.message); }
    setAvatarUploading(false);
    e.target.value = '';
  };

  const BLANK_ENTRY = { title:'', event_type:'booking', date_start:'', date_end:'', visibility:'private', counterpartId:'' };

  const saveEntry = async () => {
    if (newEntry.event_type === 'booking' && !editingEntry && !newEntry.counterpartId) { toast.error(p?.type === 'artist' ? 'Choisir un lieu' : 'Choisir un artiste'); return; }
    if (!newEntry.title.trim()) { toast.error('Titre requis'); return; }
    if (!newEntry.date_start)   { toast.error('Date de début requise'); return; }
    setAddingEntry(true);
    try {
      const base = {
        title:      newEntry.title.trim(),
        event_type: newEntry.event_type,
        date_start: newEntry.date_start + 'T00:00',
        date_end:   (newEntry.date_end || newEntry.date_start) + 'T23:59',
        visibility: newEntry.visibility,
        color:      CAL_TYPE_COLORS[newEntry.event_type] || C.orange,
      };
      if (editingEntry) {
        const updated = await updateCalendarEntry(editingEntry.id, { ...base, user_id: clientId });
        setCalEntries(prev => prev.map(e => e.id === editingEntry.id ? updated : e));
        toast.success('Entrée mise à jour !');
      } else {
        const inserts = [addCalendarEntry({ ...base, user_id: clientId })];
        if (newEntry.event_type === 'booking' && newEntry.counterpartId) {
          inserts.push(addCalendarEntry({ ...base, user_id: newEntry.counterpartId }));
          if (agentProfile?.id) inserts.push(addCalendarEntry({ ...base, user_id: agentProfile.id }));
        }
        const [primary] = await Promise.all(inserts);
        setCalEntries(prev => [...prev, primary].sort((a, b) => a.date_start.localeCompare(b.date_start)));
        toast.success(inserts.length > 1 ? `Ajouté sur ${inserts.length} agendas !` : 'Entrée ajoutée !');
      }
      setNewEntry(BLANK_ENTRY);
      setShowAddForm(false);
      setEditingEntry(null);
    } catch (e) { toast.error('Erreur: ' + e.message); }
    setAddingEntry(false);
  };

  const startEdit = (entry) => {
    setEditingEntry(entry);
    setNewEntry({
      title:        entry.title,
      event_type:   entry.event_type,
      date_start:   entry.date_start?.slice(0, 10) || '',
      date_end:     entry.date_end?.slice(0, 10) || '',
      visibility:   entry.visibility || 'private',
      counterpartId: '',
    });
    setShowAddForm(true);
  };

  const cancelForm = () => { setShowAddForm(false); setEditingEntry(null); setNewEntry(BLANK_ENTRY); };

  const handleCounterpartChange = (id) => {
    const cp = allClients.find(c => c.id === id);
    setNewEntry(prev => ({
      ...prev,
      counterpartId: id,
      title: id && cp
        ? (p?.type === 'artist' ? `${p.name} at ${cp.name}` : `${cp.name} at ${p.name}`)
        : '',
    }));
  };

  const removeEntry = async (id) => {
    if (!window.confirm('Supprimer cette entrée ?')) return;
    try {
      await deleteCalendarEntry(id);
      setCalEntries(prev => prev.filter(e => e.id !== id));
      toast.success('Entrée supprimée');
    } catch (e) { toast.error('Erreur: ' + e.message); }
  };

  const tabBtn = (key, label) => (
    <button onClick={() => setView(key)} style={{ flex:1, padding:'10px 0', background:'none', border:'none', borderBottom:'2px solid '+(view===key ? C.orange : 'transparent'), color: view===key ? C.orange : C.muted, fontFamily:"'Outfit',sans-serif", fontSize:13, fontWeight: view===key ? 700 : 400, cursor:'pointer', transition:'all .15s' }}>
      {label}
    </button>
  );

  const inputStyle = { background:C.tag, border:'1px solid '+C.border, borderRadius:8, padding:'8px 12px', color:C.text, fontFamily:"'Outfit',sans-serif", fontSize:13, outline:'none', width:'100%' };

  return (
    <div style={{ position:'fixed', inset:0, zIndex:900, display:'flex', alignItems:'flex-start', justifyContent:'flex-end' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background:C.card, width:'100%', maxWidth:480, height:'100vh', overflowY:'auto', borderLeft:'1px solid '+C.border, boxShadow:'-8px 0 40px #00000080', display:'flex', flexDirection:'column' }}>

        {/* Header */}
        <div style={{ padding:'20px 24px 0', borderBottom:'1px solid '+C.border, position:'sticky', top:0, background:C.card, zIndex:10 }}>
          <div style={{ display:'flex', alignItems:'center', gap:12, paddingBottom:12 }}>
            <button onClick={onClose} style={{ background:'none', border:'none', color:C.muted, cursor:'pointer', fontSize:20, padding:0, lineHeight:1 }}>←</button>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:700, fontSize:15, color:C.text }}>Gérer le client</div>
              {p && <div style={{ fontSize:11, color:C.dim }}>{typeLabels[p.type] || p.type} · {p.name}</div>}
            </div>
            {p && <a href={'/profile/'+p.id} target="_blank" rel="noreferrer" style={{ fontSize:11, color:C.orange, textDecoration:'none' }}>Voir public ↗</a>}
          </div>
          <div style={{ display:'flex' }}>
            {tabBtn('profile', '👤 Profil')}
            {tabBtn('calendar', '📅 Agenda')}
          </div>
        </div>

        {loading && <div style={{ display:'flex', justifyContent:'center', padding:40 }}><Spinner /></div>}

        {/* ── Profile tab ── */}
        {view === 'profile' && p && (
          <div style={{ padding:24, display:'flex', flexDirection:'column', gap:16, flex:1 }}>
            <div style={{ display:'flex', alignItems:'center', gap:16 }}>
              <div style={{ width:64, height:64, borderRadius:'50%', overflow:'hidden', background:C.border, display:'flex', alignItems:'center', justifyContent:'center', fontSize:32, flexShrink:0 }}>
                {p.avatar_url ? <img src={p.avatar_url} style={{ width:'100%', height:'100%', objectFit:'cover' }} alt="" /> : p.avatar || '🎵'}
              </div>
              <div>
                <label style={{ cursor:'pointer' }}>
                  <div style={{ background:C.tag, border:'1px solid '+C.border, borderRadius:8, padding:'6px 14px', fontSize:12, color:C.muted, display:'inline-block' }}>
                    {avatarUploading ? '⏳ Upload…' : '📷 Changer la photo'}
                  </div>
                  <input type="file" accept="image/*" onChange={handleAvatarUpload} style={{ display:'none' }} disabled={avatarUploading} />
                </label>
                <div style={{ fontSize:10, color:C.dim, marginTop:4 }}>ou choisir un emoji ↓</div>
              </div>
            </div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
              {AVATARS.map(a => (
                <button key={a} onClick={() => setField('avatar', a)}
                  style={{ fontSize:18, width:36, height:36, background:p.avatar===a?C.orange+'33':C.tag, border:'1px solid '+(p.avatar===a?C.orange:C.border), borderRadius:6, cursor:'pointer' }}>{a}</button>
              ))}
            </div>
            {[
              { k:'name',  l:'Nom',                        ml:false },
              { k:'bio',   l:'Bio',                        ml:true  },
              { k:'genre', l:'Genre',                      ml:false },
              { k:'region',l:'Ville / Région',             ml:false },
              { k:'fee',   l:'Cachet indicatif',           ml:false },
              { k:'links', l:'Liens (séparés par virgule)',ml:false },
            ].map(f => (
              <div key={f.k}>
                <div style={{ fontSize:10, color:C.dim, letterSpacing:1, textTransform:'uppercase', marginBottom:5 }}>{f.l}</div>
                <Inp value={p[f.k]||''} onChange={v => setField(f.k, v)} placeholder={f.l+'…'} ml={f.ml} rows={3}/>
              </div>
            ))}
            <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer' }}>
              <input type="checkbox" checked={!!p.available} onChange={e => setField('available', e.target.checked)} style={{ accentColor:C.orange, width:16, height:16 }}/>
              <span style={{ fontSize:13, color:C.text }}>Disponible pour booking</span>
            </label>
          </div>
        )}
        {view === 'profile' && p && (
          <div style={{ padding:'16px 24px', borderTop:'1px solid '+C.border, position:'sticky', bottom:0, background:C.card }}>
            <Btn onClick={save} disabled={saving} full>{saving ? '⏳ Sauvegarde…' : '💾 Sauvegarder'}</Btn>
          </div>
        )}

        {/* ── Calendar tab ── */}
        {view === 'calendar' && (
          <div style={{ padding:20, display:'flex', flexDirection:'column', gap:14, flex:1 }}>

            {/* Add / Edit form */}
            {showAddForm ? (
              <div style={{ background:C.tag, border:'1px solid '+C.border, borderRadius:12, padding:18, display:'flex', flexDirection:'column', gap:12 }}>
                <div style={{ fontWeight:700, fontSize:13, color:C.text }}>{editingEntry ? '✏️ Modifier l\'entrée' : '➕ Nouvelle entrée'}</div>

                {/* Title row: inline "Artist at [venue dropdown]" or "[artist dropdown] at Venue" for booking; plain input otherwise */}
                {newEntry.event_type === 'booking' && !editingEntry ? (() => {
                  const opts = allClients.filter(c => p?.type === 'artist' ? c.type === 'venue' : c.type === 'artist');
                  const isArtist = p?.type === 'artist';
                  return (
                    <div>
                      <div style={{ fontSize:10, color:C.dim, letterSpacing:1, textTransform:'uppercase', marginBottom:5 }}>
                        Booking
                        {newEntry.counterpartId && <span style={{ marginLeft:6, color:C.orange, fontWeight:600, fontSize:9 }}>→ ajouté sur 3 agendas</span>}
                      </div>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        {isArtist ? (
                          <>
                            <span style={{ whiteSpace:'nowrap', fontSize:13, fontWeight:700, color:C.text, background:C.tag, border:'1px solid '+C.border, borderRadius:8, padding:'8px 12px', flexShrink:0 }}>{p?.name}</span>
                            <span style={{ fontSize:12, color:C.dim, flexShrink:0 }}>at</span>
                            <select value={newEntry.counterpartId} onChange={e => handleCounterpartChange(e.target.value)} style={{ ...inputStyle, flex:1 }}>
                              <option value="">— Choisir un lieu —</option>
                              {opts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                          </>
                        ) : (
                          <>
                            <select value={newEntry.counterpartId} onChange={e => handleCounterpartChange(e.target.value)} style={{ ...inputStyle, flex:1 }}>
                              <option value="">— Choisir un artiste —</option>
                              {opts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                            <span style={{ fontSize:12, color:C.dim, flexShrink:0 }}>at</span>
                            <span style={{ whiteSpace:'nowrap', fontSize:13, fontWeight:700, color:C.text, background:C.tag, border:'1px solid '+C.border, borderRadius:8, padding:'8px 12px', flexShrink:0 }}>{p?.name}</span>
                          </>
                        )}
                      </div>
                      {!opts.length && <div style={{ fontSize:11, color:C.dim, marginTop:6 }}>Aucun {isArtist ? 'lieu' : 'artiste'} géré — invitez-en d'abord depuis l'onglet Agence.</div>}
                    </div>
                  );
                })() : (
                  <div>
                    <div style={{ fontSize:10, color:C.dim, letterSpacing:1, textTransform:'uppercase', marginBottom:5 }}>Titre</div>
                    <input value={newEntry.title} onChange={e => setNE('title', e.target.value)} placeholder="Titre…" style={inputStyle} />
                  </div>
                )}

                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  <div>
                    <div style={{ fontSize:10, color:C.dim, letterSpacing:1, textTransform:'uppercase', marginBottom:5 }}>Type</div>
                    <select value={newEntry.event_type} onChange={e => { setNE('event_type', e.target.value); setNE('counterpartId', ''); setNE('title', ''); }} style={inputStyle}>
                      {Object.entries(CAL_TYPE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                  </div>
                  <div>
                    <div style={{ fontSize:10, color:C.dim, letterSpacing:1, textTransform:'uppercase', marginBottom:5 }}>Visibilité</div>
                    <select value={newEntry.visibility} onChange={e => setNE('visibility', e.target.value)} style={inputStyle}>
                      <option value="private">Privé</option>
                      <option value="shared">Partagé</option>
                      <option value="public">Public</option>
                    </select>
                  </div>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  <div>
                    <div style={{ fontSize:10, color:C.dim, letterSpacing:1, textTransform:'uppercase', marginBottom:5 }}>Date début</div>
                    <input type="date" value={newEntry.date_start} onChange={e => setNE('date_start', e.target.value)} style={inputStyle} />
                  </div>
                  <div>
                    <div style={{ fontSize:10, color:C.dim, letterSpacing:1, textTransform:'uppercase', marginBottom:5 }}>Date fin</div>
                    <input type="date" value={newEntry.date_end} onChange={e => setNE('date_end', e.target.value)} style={inputStyle} />
                  </div>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <Btn onClick={saveEntry} disabled={addingEntry} full>{addingEntry ? '⏳…' : editingEntry ? '💾 Mettre à jour' : '➕ Ajouter'}</Btn>
                  <Btn onClick={cancelForm} v="ghost">Annuler</Btn>
                </div>
              </div>
            ) : (
              <button onClick={() => setShowAddForm(true)} style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:8, background:C.orange+'15', border:'1px dashed '+C.orange+'80', borderRadius:10, padding:'10px 0', color:C.orange, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:"'Outfit',sans-serif" }}>
                ➕ Ajouter une entrée à l'agenda
              </button>
            )}

            {calLoading && <div style={{ display:'flex', justifyContent:'center', padding:20 }}><Spinner /></div>}

            {!calLoading && calEntries.length === 0 && !showAddForm && (
              <div style={{ textAlign:'center', color:C.dim, fontSize:13, padding:'20px 0' }}>Aucune entrée dans l'agenda.</div>
            )}

            {!calLoading && calEntries.length > 0 && (() => {
              const now = new Date();
              const upcoming = calEntries.filter(e => new Date(e.date_start) >= now);
              const past     = calEntries.filter(e => new Date(e.date_start) < now);
              const EntryRow = ({ e }) => (
                <div key={e.id} style={{ display:'flex', alignItems:'center', gap:10, background:C.tag, borderRadius:8, padding:'10px 12px' }}>
                  <div style={{ width:8, height:8, borderRadius:'50%', background: CAL_TYPE_COLORS[e.event_type] || C.orange, flexShrink:0 }} />
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:600, color:C.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{e.title}</div>
                    <div style={{ fontSize:10, color:C.dim, marginTop:2 }}>
                      {CAL_TYPE_LABELS[e.event_type] || e.event_type} · {fmtDate(e.date_start)}{e.date_end && e.date_end.slice(0,10) !== e.date_start.slice(0,10) ? ' → ' + fmtDate(e.date_end) : ''}
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:5, flexShrink:0 }}>
                    <button onClick={() => startEdit(e)} style={{ background:'none', border:'1px solid '+C.border, borderRadius:5, color:C.muted, padding:'3px 8px', fontSize:11, cursor:'pointer' }}>✏️</button>
                    <button onClick={() => removeEntry(e.id)} style={{ background:'none', border:'1px solid '+C.red+'44', borderRadius:5, color:C.red, padding:'3px 8px', fontSize:11, cursor:'pointer' }}>✕</button>
                  </div>
                </div>
              );
              return (
                <>
                  {upcoming.length > 0 && (
                    <div>
                      <div style={{ fontSize:10, color:C.dim, letterSpacing:1, textTransform:'uppercase', marginBottom:8 }}>À venir ({upcoming.length})</div>
                      <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                        {upcoming.map(e => <EntryRow key={e.id} e={e} />)}
                      </div>
                    </div>
                  )}
                  {past.length > 0 && (
                    <div>
                      <div style={{ fontSize:10, color:C.dim, letterSpacing:1, textTransform:'uppercase', marginBottom:8, marginTop:4 }}>Passés ({past.length})</div>
                      <div style={{ display:'flex', flexDirection:'column', gap:6, opacity:0.6 }}>
                        {past.slice().reverse().map(e => <EntryRow key={e.id} e={e} />)}
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Agency Tab ── */
function AgencyTab({ profile }) {
  const [clients, setClients] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newType, setNewType] = useState('artist');
  const [copied, setCopied] = useState(null);
  const [managingId, setManagingId] = useState(null);

  const reload = () => {
    Promise.all([getAgentClients(profile.id), getAgentTokens(profile.id)])
      .then(([c, t]) => { setClients(c); setTokens(t); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(); }, [profile.id]);

  const artistCount = clients.filter(c => c.type === 'artist').length;
  const venueCount  = clients.filter(c => c.type === 'venue').length;

  const createLink = async () => {
    setCreating(true);
    try {
      const tk = await createInviteToken(profile.id, profile.name, newType);
      const link = window.location.origin + '/invite/' + tk.token;
      setTokens(prev => [tk, ...prev]);
      await navigator.clipboard.writeText(link);
      toast.success('Lien copié dans le presse-papiers !');
    } catch (e) {
      toast.error('Erreur: ' + e.message);
    }
    setCreating(false);
  };

  const copyLink = async (token) => {
    const link = window.location.origin + '/invite/' + token;
    await navigator.clipboard.writeText(link);
    setCopied(token);
    setTimeout(() => setCopied(null), 2000);
  };

  if (loading) return <div style={{ display:'flex', justifyContent:'center', padding:40 }}><Spinner /></div>;

  return (
    <div className="fade-in" style={{ maxWidth: 700, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20 }}>
      {managingId && (
        <ClientProfileManager
          clientId={managingId}
          agentProfile={profile}
          allClients={clients}
          onClose={() => setManagingId(null)}
          onSaved={() => { setManagingId(null); reload(); }}
        />
      )}

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        {[
          { label: 'Artistes', count: artistCount, max: 15, color: C.orange, icon: '🎵' },
          { label: 'Lieux',    count: venueCount,  max: 15, color: C.brown,  icon: '🏛️' },
          { label: 'Liens actifs', count: tokens.filter(t => !t.used_by).length, max: null, color: '#f5c842', icon: '🔗' },
        ].map(s => (
          <div key={s.label} style={{ background: C.card, border: '1px solid ' + C.border, borderRadius: 14, padding: '18px 16px', textAlign: 'center' }}>
            <div style={{ fontSize: 28, marginBottom: 6 }}>{s.icon}</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: s.color, fontFamily: "'Cormorant Garamond',serif" }}>
              {s.count}{s.max && <span style={{ fontSize: 14, color: C.dim }}>/{s.max}</span>}
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{s.label}</div>
            {s.max && (
              <div style={{ marginTop: 8, height: 3, background: C.border, borderRadius: 2 }}>
                <div style={{ height: 3, background: s.color, borderRadius: 2, width: Math.min(100, (s.count / s.max) * 100) + '%', transition: 'width .4s' }} />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Generate link */}
      <div style={{ background: C.card, border: '1px solid ' + C.border, borderRadius: 14, padding: 24 }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: C.text, marginBottom: 14 }}>🔗 Générer un lien d'invitation</div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={newType} onChange={e => setNewType(e.target.value)}
            style={{ background: C.tag, border: '1px solid ' + C.border, borderRadius: 8, padding: '9px 12px', color: C.text, fontFamily: "'Outfit',sans-serif", fontSize: 13, outline: 'none', flex: 1, minWidth: 140 }}>
            <option value="artist">🎵 Artiste</option>
            <option value="venue">🏛️ Lieu / Venue</option>
          </select>
          <Btn onClick={createLink} disabled={creating || (newType === 'artist' && artistCount >= 15) || (newType === 'venue' && venueCount >= 15)}>
            {creating ? '...' : '✨ Créer & copier le lien'}
          </Btn>
        </div>
        {((newType === 'artist' && artistCount >= 15) || (newType === 'venue' && venueCount >= 15)) && (
          <div style={{ marginTop: 10, fontSize: 12, color: C.red }}>Limite de 15 {newType === 'artist' ? 'artistes' : 'lieux'} atteinte.</div>
        )}
        <div style={{ marginTop: 12, fontSize: 12, color: C.dim }}>
          Le lien invite votre client à créer son propre compte et profil, automatiquement lié à votre agence.
        </div>
      </div>

      {/* Token history */}
      {tokens.length > 0 && (
        <div style={{ background: C.card, border: '1px solid ' + C.border, borderRadius: 14, padding: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: C.text, marginBottom: 12 }}>Liens envoyés</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 240, overflowY: 'auto' }}>
            {tokens.map(tk => (
              <div key={tk.id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: C.tag, borderRadius: 8, padding: '8px 12px' }}>
                <span style={{ fontSize: 16 }}>{tk.profile_type === 'artist' ? '🎵' : '🏛️'}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {window.location.origin}/invite/{tk.token}
                  </div>
                  <div style={{ fontSize: 10, color: C.dim, marginTop: 2 }}>
                    {tk.used_by ? '✅ Utilisé' : `Expire le ${new Date(tk.expires_at).toLocaleDateString('fr')}`}
                  </div>
                </div>
                {!tk.used_by && (
                  <button onClick={() => copyLink(tk.token)}
                    style={{ background: copied === tk.token ? C.green + '22' : C.tag, border: '1px solid ' + (copied === tk.token ? C.green : C.border), borderRadius: 6, color: copied === tk.token ? C.green : C.muted, padding: '4px 10px', fontSize: 11, cursor: 'pointer', fontFamily: "'Outfit',sans-serif", whiteSpace: 'nowrap' }}>
                    {copied === tk.token ? '✓ Copié' : '📋 Copier'}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Client roster */}
      {clients.length > 0 && (
        <div style={{ background: C.card, border: '1px solid ' + C.border, borderRadius: 14, padding: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: C.text, marginBottom: 12 }}>Mes clients ({clients.length})</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {clients.map(c => (
              <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12, background: C.tag, borderRadius: 8, padding: '10px 14px' }}>
                {c.avatar_url
                  ? <img src={c.avatar_url} style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} alt="" />
                  : <div style={{ width: 36, height: 36, borderRadius: '50%', background: C.border, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>{c.type === 'artist' ? '🎵' : '🏛️'}</div>}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{c.name}</div>
                  <div style={{ fontSize: 11, color: C.dim }}>{typeLabels[c.type] || c.type} · {c.region || '—'}</div>
                </div>
                <button onClick={() => setManagingId(c.id)}
                  style={{ background: C.orange+'22', border:'1px solid '+C.orange+'66', borderRadius:7, color:C.orange, padding:'5px 12px', fontSize:11, fontWeight:600, cursor:'pointer', fontFamily:"'Outfit',sans-serif", whiteSpace:'nowrap' }}>
                  Gérer →
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {clients.length === 0 && (
        <div style={{ textAlign: 'center', color: C.dim, padding: '30px 0', fontSize: 13 }}>
          Aucun client encore lié — partagez un lien d'invitation pour commencer.
        </div>
      )}
    </div>
  );
}

/* ── Promo Module ── */
function PromoModule({ myProfile, isSubscribed }) {
  const [pkg, setPkg] = useState(null);
  const [step, setStep] = useState('list');
  const t = useT();
  const [ev, setEv] = useState({ title:'', date:'', venue:'', city:'', genre:'', desc:'', visual:'🎷', budget:89 });
  const [pl, setPl] = useState({ stagemap:true, meta:false, google:false, tiktok:false, spotify:false });
  const totalBudget = Object.entries(pl).filter(([k,v])=>v&&k!=='stagemap')?.length*30+ev.budget;

  const launch = async () => {
    try {
      await payForCampaign({
        package_id: pkg.id,
        title: ev.title,
        date: ev.date,
        venue: ev.venue,
        city: ev.city,
        genre: ev.genre,
        description: ev.desc,
        platforms: Object.keys(pl).filter(k => pl[k]),
        budget: totalBudget,
      }, myProfile.id);
    }
    catch(e) { toast.error('Erreur paiement: '+e.message); }
  };

  return (
    <div className='fade-in'>
      <div style={{marginBottom:18,display:'flex',gap:12,alignItems:'center'}}>
        <span style={{fontSize:28}}>📣</span>
        <div>
          <h2 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:20,color:C.cream,fontWeight:700}}>{t('promo.title')}</h2>
          <p style={{color:C.muted,fontSize:12,marginTop:2}}>{t('promo.subtitle')}</p>
        </div>
      </div>
      {step==='list'&&<div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(250px,1fr))',gap:14}}>
        {AD_PACKAGES.map(p=>(
          <div key={p.id} onClick={()=>{setPkg(p);setStep('builder');}} style={{background:C.card,border:'2px solid '+C.border,borderRadius:14,padding:20,cursor:'pointer',transition:'all .2s',position:'relative'}}
            onMouseEnter={e=>{e.currentTarget.style.borderColor=p.color;e.currentTarget.style.transform='translateY(-3px)';}}
            onMouseLeave={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.transform='none';}}>
            {p.badge&&<div style={{position:'absolute',top:-10,right:14,background:C.orange,color:'#fff',borderRadius:20,padding:'2px 10px',fontSize:10,fontWeight:700}}>★ {p.badge}</div>}
            <div style={{display:'flex',gap:10,alignItems:'center',marginBottom:10}}>
              <span style={{fontSize:26}}>{p.icon}</span>
              <div>
                <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:16,fontWeight:700,color:C.cream}}>{p.name}</div>
                <Pill color={p.color} sm>{p.reach}</Pill>
              </div>
            </div>
            <div style={{marginBottom:10}}>
              <span style={{fontFamily:"'Cormorant Garamond',serif",fontSize:28,fontWeight:700,color:p.color}}>{p.price}$</span>
              <span style={{color:C.muted,fontSize:11,marginLeft:4}}>CA/événement</span>
            </div>
            <div style={{marginBottom:10}}>{p.features.map((f,i)=><div key={i} style={{display:'flex',gap:6,marginBottom:3,fontSize:11,color:C.text}}><span style={{color:p.color}}>✓</span>{f}</div>)}</div>
            <div style={{background:C.tag,borderRadius:6,padding:'5px 9px',fontSize:11,color:C.muted}}>{p.roi}</div>
          </div>
        ))}
      </div>}
      {step==='builder'&&<div>
        <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:16}}>
          <button onClick={()=>setStep('list')} style={{background:'none',border:'none',color:C.muted,cursor:'pointer',fontSize:18}}>←</button>
          <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:17,color:C.cream,fontWeight:700}}>Configurer — {pkg&&pkg.name}</div>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:18}}>
          <div style={{display:'flex',flexDirection:'column',gap:10}}>
            {[{l:'Titre',k:'title',p:'Ex: Soirée Jazz'},{l:'Date',k:'date',p:'2026-06-15'},{l:'Lieu',k:'venue',p:'Le Café Scène'},{l:'Ville',k:'city',p:'Montréal, QC'},{l:'Genre',k:'genre',p:'Jazz...'}].map(f=>(
              <div key={f.k}>
                <div style={{fontSize:10,color:C.dim,marginBottom:3,textTransform:'uppercase',letterSpacing:1}}>{f.l}</div>
                <Inp value={ev[f.k]} onChange={v=>setEv(d=>({...d,[f.k]:v}))} placeholder={f.p}/>
              </div>
            ))}
          </div>
          <div>
            <div style={{fontSize:10,letterSpacing:2,textTransform:'uppercase',color:C.dim,marginBottom:10,fontWeight:600}}>Plateformes</div>
            {[{k:'stagemap',l:'StageMap',c:C.orange},{k:'meta',l:'Meta/Instagram',c:'#1877f2'},{k:'google',l:'Google Ads',c:'#34a853'},{k:'tiktok',l:'TikTok',c:'#ff0050'},{k:'spotify',l:'Spotify',c:'#1db954'}].map(p=>(
              <div key={p.k} onClick={()=>setPl(prev=>({...prev,[p.k]:!prev[p.k]}))} style={{background:pl[p.k]?p.c+'11':C.tag,border:'1px solid '+(pl[p.k]?p.c:C.border),borderRadius:8,padding:'8px 12px',marginBottom:7,cursor:'pointer',display:'flex',alignItems:'center',gap:9}}>
                <div style={{flex:1,fontSize:12,color:pl[p.k]?C.text:C.muted,fontWeight:pl[p.k]?600:400}}>{p.l}</div>
                <div style={{width:16,height:16,borderRadius:4,background:pl[p.k]?p.c:C.bg,border:'2px solid '+(pl[p.k]?p.c:C.border),display:'flex',alignItems:'center',justifyContent:'center',fontSize:9,color:'#fff'}}>{pl[p.k]?'✓':''}</div>
              </div>
            ))}
            <div style={{marginTop:14,background:C.card,border:'1px solid '+C.border,borderRadius:10,padding:14}}>
              <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:24,color:(pkg&&pkg.color)||C.orange,fontWeight:700,marginBottom:4}}>{totalBudget}$ CA</div>
              <div style={{fontSize:11,color:C.muted}}>ROI estimé : {(totalBudget*4).toLocaleString()}$ – {(totalBudget*7).toLocaleString()}$</div>
            </div>
          </div>
        </div>
        <div style={{display:'flex',gap:10,marginTop:16}}>
          <Btn onClick={launch} sz='lg'>{t('promo.pay')}</Btn>
          <Btn v='secondary' onClick={()=>setStep('list')}>← Changer</Btn>
        </div>
      </div>}
    </div>
  );
}

/* ── Avatar Position Editor ── */
function AvatarPositionEditor({ src, position, onChange }) {
  const [pos, setPos] = useState(position || { x: 50, y: 50 });
  const [dragging, setDragging] = useState(false);
  const startRef = useRef(null);

  const getCoords = (e) => e.touches ? { x: e.touches[0].clientX, y: e.touches[0].clientY } : { x: e.clientX, y: e.clientY };

  const onStart = (e) => {
    e.preventDefault();
    startRef.current = { ...getCoords(e), pos: { ...pos } };
    setDragging(true);
  };
  const onMove = (e) => {
    if (!startRef.current) return;
    e.preventDefault();
    const c = getCoords(e);
    const newPos = {
      x: Math.max(0, Math.min(100, startRef.current.pos.x - (c.x - startRef.current.x) * 0.25)),
      y: Math.max(0, Math.min(100, startRef.current.pos.y - (c.y - startRef.current.y) * 0.25)),
    };
    setPos(newPos);
    onChange(newPos);
  };
  const onEnd = () => { setDragging(false); startRef.current = null; };

  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:5 }}>
      <div
        onMouseDown={onStart} onMouseMove={dragging ? onMove : undefined} onMouseUp={onEnd} onMouseLeave={onEnd}
        onTouchStart={onStart} onTouchMove={dragging ? onMove : undefined} onTouchEnd={onEnd}
        style={{ width:96, height:96, borderRadius:'50%', overflow:'hidden', cursor:dragging?'grabbing':'grab', border:'2px dashed '+C.orange, flexShrink:0, userSelect:'none' }}>
        <img src={src} alt="" draggable={false} style={{ width:'100%', height:'100%', objectFit:'cover', objectPosition:`${pos.x}% ${pos.y}%`, pointerEvents:'none' }}/>
      </div>
      <div style={{ fontSize:10, color:C.dim }}>↔ Glisser pour recadrer</div>
    </div>
  );
}

/* ── My Profile Tab ── */
const geocodePostal = async (code) => {
  const token = process.env.REACT_APP_MAPBOX_TOKEN;
  if (!token || !code.trim()) return null;
  try {
    const res = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(code.trim())}.json?types=postcode,place&access_token=${token}`
    );
    const data = await res.json();
    if (!data.features?.length) return null;
    const f = data.features[0];
    const [lng, lat] = f.center;
    const place = f.context?.find(c => c.id.startsWith('place')) || f.context?.find(c => c.id.startsWith('region'));
    const region = place?.text || f.text || code;
    return { lat, lng, region };
  } catch { return null; }
};

function MyProfileTab({ profile, userProfiles, setProfile, user, onLogout, onAddProfile, isSubscribed }) {
  const { updateUserProfile } = useStore();
  const t = useT();
  const tl = { artist: t('type.artist'), venue: t('type.venue'), fan: t('type.fan') };
  const [p, setP] = useState({
    ...profile,
    links: Array.isArray(profile.links) ? profile.links.join(', ') : profile.links || '',
    profile_visibility: profile.profile_visibility || {},
    postal_code: profile.postal_code || '',
  });
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoCity, setGeoCity] = useState(profile.region || '');
  const geoTimer = useRef(null);
  const set = (k, v) => setP(prev => ({ ...prev, [k]: v }));

  const handlePostalChange = (code) => {
    set('postal_code', code);
    clearTimeout(geoTimer.current);
    if (code.trim().length < 3) { setGeoCity(''); return; }
    geoTimer.current = setTimeout(async () => {
      setGeoLoading(true);
      const result = await geocodePostal(code);
      if (result) {
        setP(prev => ({ ...prev, lat: result.lat, lng: result.lng, region: result.region }));
        setGeoCity(result.region);
      } else {
        setGeoCity('introuvable');
      }
      setGeoLoading(false);
    }, 600);
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAvatarUploading(true);
    try {
      const url = await uploadAvatar(p.id, file);
      set('avatar_url', url);
      await upsertProfile({ id: p.id, avatar_url: url });
      toast.success('Photo mise à jour !');
    } catch (err) {
      toast.error('Erreur upload: ' + err.message);
    }
    setAvatarUploading(false);
    e.target.value = '';
  };

  const [galleryUploading, setGalleryUploading] = useState(false);
  const handleGalleryUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const gallery = p.gallery_urls || [];
    if (gallery.length >= 5) { toast.error('Maximum 5 photos'); return; }
    setGalleryUploading(true);
    try {
      const url = await uploadGalleryPhoto(p.id, file);
      const newGallery = [...gallery, url];
      set('gallery_urls', newGallery);
      await upsertProfile({ id: p.id, gallery_urls: newGallery });
      toast.success('Photo ajoutée !');
    } catch (err) {
      toast.error('Erreur upload: ' + err.message);
    }
    setGalleryUploading(false);
    e.target.value = '';
  };
  const removeGalleryPhoto = async (i) => {
    const gallery = [...(p.gallery_urls || [])];
    gallery.splice(i, 1);
    set('gallery_urls', gallery);
    try {
      await upsertProfile({ id: p.id, gallery_urls: gallery });
    } catch (err) {
      toast.error('Erreur: ' + err.message);
    }
  };

  const setVis = (k, v) => setP(prev => ({ ...prev, profile_visibility: { ...prev.profile_visibility, [k]: v } }));

  const save = async () => {
    setLoading(true);
    try {
      const fallback = REGION_COORDS[p.region] || { lat: p.lat || 45.5, lng: p.lng || -73.57 };
      const updated = await upsertProfile({
        ...p,
        links: p.links ? p.links.split(',').map(l => l.trim()).filter(Boolean) : [],
        lat: p.lat || fallback.lat,
        lng: p.lng || fallback.lng,
      });
      updateUserProfile(updated);
      setProfile(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      toast.success(t('profile_updated'));
    } catch(e) { toast.error(e.message); }
    setLoading(false);
  };

  const vis = p.profile_visibility || {};
  const visFields = [
    { k:'show_bio',       label: t('vis.bio'),       icon:'📝' },
    { k:'show_fee',       label: t('vis.fee'),       icon:'💰' },
    { k:'show_links',     label: t('vis.links'),     icon:'🔗' },
    { k:'show_genre',     label: t('vis.genre'),     icon:'🎼' },
    { k:'show_region',    label: t('vis.region'),    icon:'📍' },
    { k:'show_available', label: t('vis.available'), icon:'✅' },
    { k:'show_events',    label: t('vis.events'),    icon:'📅' },
  ];

  return (
    <div style={{ maxWidth:680, display:'flex', flexDirection:'column', gap:16 }} className='fade-in'>

      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, color:C.cream, fontWeight:700 }}>{t('me.title')}</div>
        <div style={{ display:'flex', gap:8 }}>
          <Btn v='secondary' sz='sm' onClick={() => window.open('/profile/'+profile.id, '_blank')}>{t('me.public_page')}</Btn>
          <Btn v='ghost' sz='sm' onClick={onLogout}>{t('btn.logout')}</Btn>
        </div>
      </div>

      {/* Subscription badge */}
      <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
        <Pill color={typeColors[profile.type]}>{tl[profile.type]}</Pill>
        {isSubscribed && <Pill color={C.glow}>{t('status.subscribed')}</Pill>}
        {profile.verified && <Pill color={C.blue}>{t('status.verified')}</Pill>}
        <span style={{ fontSize:11, color:C.dim }}>📧 {user.email}</span>
      </div>

      {/* ── Profile fields ── */}
      <div style={{ background:C.card, border:'1px solid '+C.border, borderRadius:16, padding:24, display:'flex', flexDirection:'column', gap:14 }}>
        <div style={{ fontSize:10, color:C.dim, letterSpacing:1.5, textTransform:'uppercase' }}>{t('me.info')}</div>

        <div>
          <div style={{ fontSize:10, color:C.dim, letterSpacing:1, textTransform:'uppercase', marginBottom:7 }}>{t('me.avatar')}</div>
          <div style={{ display:'flex', alignItems:'flex-start', gap:14, marginBottom:10 }}>
            {p.avatar_url
              ? <AvatarPositionEditor src={p.avatar_url} position={p.avatar_position} onChange={pos => set('avatar_position', pos)}/>
              : <div style={{ width:64, height:64, borderRadius:'50%', background:C.tag, border:'2px solid '+C.orange, flexShrink:0, overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <span style={{ fontSize:28 }}>{p.avatar||'🎵'}</span>
                </div>
            }
            <div style={{ flex:1, display:'flex', flexDirection:'column', gap:6 }}>
              <label style={{ display:'block', textAlign:'center', background:C.orange+'22', border:'1px solid '+C.orange+'55', color:avatarUploading?C.dim:C.orange, borderRadius:8, padding:'7px 12px', fontSize:12, fontWeight:600, cursor:avatarUploading?'not-allowed':'pointer' }}>
                {avatarUploading ? '⏳ Envoi...' : '📷 Changer la photo'}
                <input type="file" accept="image/*" style={{ display:'none' }} onChange={handleAvatarUpload} disabled={avatarUploading}/>
              </label>
              {p.avatar_url && (
                <button onClick={() => set('avatar_url', null)} style={{ background:'none', border:'1px solid '+C.border, color:C.dim, borderRadius:8, padding:'5px 0', fontSize:11, cursor:'pointer' }}>
                  Retirer la photo
                </button>
              )}
            </div>
          </div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
            {AVATARS.map(a => (
              <button key={a} onClick={() => set('avatar', a)}
                style={{ fontSize:20, width:38, height:38, background:p.avatar===a?C.orange+'33':C.tag, border:'1px solid '+(p.avatar===a?C.orange:C.border), borderRadius:7, cursor:'pointer' }}>
                {a}
              </button>
            ))}
          </div>
        </div>

        {/* ── Gallery ── */}
        <div>
          <div style={{ fontSize:10, color:C.dim, letterSpacing:1, textTransform:'uppercase', marginBottom:7 }}>Galerie photos ({(p.gallery_urls||[]).length}/5)</div>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            {(p.gallery_urls||[]).map((url,i) => (
              <div key={i} style={{ position:'relative', width:72, height:72, borderRadius:8, overflow:'hidden', border:'1px solid '+C.border, flexShrink:0 }}>
                <img src={url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                <button onClick={() => removeGalleryPhoto(i)}
                  style={{ position:'absolute', top:2, right:2, background:'#00000099', border:'none', color:'#fff', borderRadius:'50%', width:18, height:18, fontSize:12, lineHeight:'18px', cursor:'pointer', padding:0 }}>×</button>
              </div>
            ))}
            {(p.gallery_urls||[]).length < 5 && (
              <label style={{ width:72, height:72, borderRadius:8, border:'1px dashed '+C.border, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:2, cursor:galleryUploading?'not-allowed':'pointer', color:C.dim, fontSize:10, flexShrink:0 }}>
                {galleryUploading ? '⏳' : <><span style={{ fontSize:22, lineHeight:1 }}>+</span><span>Photo</span></>}
                <input type="file" accept="image/*" style={{ display:'none' }} onChange={handleGalleryUpload} disabled={galleryUploading}/>
              </label>
            )}
          </div>
        </div>

        {[
          { l: t('me.artist_name'), k:'name' },
          { l: t('me.genre'),       k:'genre', sel:GENRES },
          { l: t('me.bio'),         k:'bio',   ml:true },
          { l: t('me.fee'),         k:'fee' },
          { l: t('me.links'),       k:'links' },
        ].map(f => (
          <div key={f.k}>
            <div style={{ fontSize:10, color:C.dim, letterSpacing:1, textTransform:'uppercase', marginBottom:5 }}>{f.l}</div>
            {f.sel
              ? <Sel value={p[f.k]||''} onChange={v => set(f.k, v)} options={f.sel}/>
              : <Inp value={p[f.k]||''} onChange={v => set(f.k, v)} placeholder={f.l+'...'} ml={f.ml} rows={3}/>}
          </div>
        ))}

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
          <div>
            <div style={{ fontSize:10, color:C.dim, letterSpacing:1, textTransform:'uppercase', marginBottom:5 }}>Code postal / ZIP</div>
            <Inp value={p.postal_code||''} onChange={handlePostalChange} placeholder="H2X 1Y6, 75001…"/>
            <div style={{ fontSize:10, marginTop:4, color: geoCity === 'introuvable' ? C.red : C.green, minHeight:14 }}>
              {geoLoading ? '⏳ Géolocalisation…' : geoCity ? '📍 ' + geoCity : ''}
            </div>
          </div>
          <div>
            <div style={{ fontSize:10, color:C.dim, letterSpacing:1, textTransform:'uppercase', marginBottom:5 }}>{t('me.country')}</div>
            <Sel value={p.country||'Canada'} onChange={v => set('country', v)} options={['Canada','France','Belgique','Suisse','Allemagne','Espagne','Pays-Bas','Autre']}/>
          </div>
        </div>

        <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer' }}>
          <input type='checkbox' checked={!!p.available} onChange={e => set('available', e.target.checked)} style={{ accentColor:C.orange, width:16, height:16 }}/>
          <span style={{ fontSize:13, color:C.text }}>{t('me.available')}</span>
        </label>

        <Btn onClick={save} disabled={loading} sz='lg'>
          {loading ? <Spinner/> : saved ? t('me.saved') : t('me.save')}
        </Btn>
      </div>

      {/* ── Visibility controls ── */}
      <div style={{ background:C.card, border:'1px solid '+C.border, borderRadius:16, padding:24 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:16 }}>
          <div>
            <div style={{ fontSize:10, color:C.dim, letterSpacing:1.5, textTransform:'uppercase', marginBottom:4 }}>{t('me.vis_title')}</div>
            <div style={{ fontSize:12, color:C.muted }}>{t('me.vis_desc')}</div>
          </div>
          <Btn v='ghost' sz='sm' onClick={() => window.open('/profile/'+profile.id, '_blank')}>{t('btn.preview')}</Btn>
        </div>

        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {visFields.map(({ k, label, icon }) => {
            const isOn = vis[k] !== false;
            return (
              <div key={k} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', background:isOn?C.green+'0a':C.tag, border:'1px solid '+(isOn?C.green+'33':C.border), borderRadius:10, transition:'all .2s' }}>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <span style={{ fontSize:15 }}>{icon}</span>
                  <span style={{ fontSize:13, color:isOn?C.text:C.muted }}>{label}</span>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ fontSize:11, color:isOn?C.green:C.dim }}>{isOn?t('me.vis_on'):t('me.vis_off')}</span>
                  <div onClick={() => setVis(k, !isOn)}
                    style={{ width:40, height:22, borderRadius:11, background:isOn?C.green:C.border, cursor:'pointer', position:'relative', transition:'background .2s', flexShrink:0 }}>
                    <div style={{ position:'absolute', top:3, left:isOn?21:3, width:16, height:16, borderRadius:'50%', background:'#fff', transition:'left .2s', boxShadow:'0 1px 4px #00000040' }}/>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ marginTop:14, fontSize:11, color:C.dim, lineHeight:1.7, background:C.tag, borderRadius:8, padding:'8px 12px' }}>
          {t('me.vis_note')}
        </div>
      </div>

      {/* ── Other profiles ── */}
      {userProfiles && userProfiles.length > 1 && (
        <div style={{ background:C.card, border:'1px solid '+C.border, borderRadius:12, padding:18 }}>
          <div style={{ fontWeight:700, color:C.text, marginBottom:12, fontSize:13 }}>{t('me.other_profiles')}</div>
          {userProfiles.filter(up => up.id !== profile.id).map(up => (
            <div key={up.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 0', borderBottom:'1px solid '+C.border+'44' }}>
              <span style={{ fontSize:22 }}>{up.avatar}</span>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13, fontWeight:600, color:C.text }}>{up.name}</div>
                <div style={{ fontSize:11, color:C.muted }}>{tl[up.type]} · {up.region}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Btn v='secondary' onClick={onAddProfile} full>{t('btn.add_profile')}</Btn>

      {/* ── Artist tier picker (artists only) ── */}
      {profile.type === 'artist' && (
        <div style={{ background:C.card, border:'1px solid '+C.border, borderRadius:16, padding:24 }}>
          <div style={{ fontSize:10, color:C.dim, letterSpacing:1.5, textTransform:'uppercase', marginBottom:4 }}>Niveau artiste</div>
          <div style={{ fontSize:12, color:C.muted, marginBottom:16, lineHeight:1.6 }}>Choisissez votre niveau pour définir l'accès de vos fans à votre profil.</div>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {[
              { k:'all_stars', color:C.purple, price:'29$ / mois', label:'⭐ All Stars — Professionnel', desc:'Profil complet · Booking · Agenda · Chat · Répertoire' },
              { k:'local_legends', color:C.orange, price:'15$ / mois', label:'🎵 Local Legends — Semi-pro', desc:'Profil complet · Agenda partagé · Chat · Répertoire' },
              { k:'amateur', color:C.muted, price:'Gratuit', label:'🎶 Amateur', desc:'Répertoire public uniquement' },
            ].map(tier => {
              const isActive = (p.artist_tier || 'amateur') === tier.k;
              return (
                <div key={tier.k} onClick={() => set('artist_tier', tier.k)}
                  style={{ display:'flex', alignItems:'center', gap:12, padding:14, background:isActive?tier.color+'18':C.tag, border:'1px solid '+(isActive?tier.color:C.border), borderRadius:10, cursor:'pointer', transition:'all .15s' }}>
                  <div style={{ width:18, height:18, borderRadius:'50%', border:'2px solid '+(isActive?tier.color:C.dim), background:isActive?tier.color:'none', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
                    {isActive && <div style={{ width:6, height:6, borderRadius:'50%', background:'#fff' }}/>}
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, fontWeight:600, color:isActive?tier.color:C.text }}>{tier.label}</div>
                    <div style={{ fontSize:11, color:C.dim, marginTop:2 }}>{tier.desc}</div>
                  </div>
                  <div style={{ fontSize:12, fontWeight:600, color:isActive?tier.color:C.muted, flexShrink:0 }}>{tier.price}</div>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop:12, fontSize:11, color:C.dim, lineHeight:1.6, background:C.tag, borderRadius:8, padding:'7px 11px' }}>
            Sauvegardez votre profil après avoir changé de niveau. Les fans existants conservent leur accès jusqu'à expiration.
          </div>
        </div>
      )}

      {!isSubscribed && (
        <div style={{ background:C.card, border:'1px solid '+C.border, borderRadius:12, padding:18 }}>
          <div style={{ fontWeight:700, color:C.orange, marginBottom:6, fontSize:14 }}>{t('me.subscribe')}</div>
          <div style={{ color:C.muted, fontSize:12, marginBottom:14, lineHeight:1.7 }}>{t('me.subscribe_desc')}</div>
          <div style={{ display:'flex', gap:10 }}>
            <Btn onClick={() => startSubscription('monthly', profile.id).catch(e => toast.error(e.message))}>{t('me.monthly')}</Btn>
            <Btn v='secondary' onClick={() => startSubscription('annual', profile.id).catch(e => toast.error(e.message))}>{t('me.annual')}</Btn>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Upgrade Modal ── */
function UpgradeModal({ reason, profile, onClose }) {
  const isFan = reason === 'fan';
  const t = useT();
  return (
    <div style={{ position:'fixed', inset:0, background:'#00000090', zIndex:3000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{ background:C.bg2, border:'1px solid '+C.border, borderRadius:16, maxWidth:420, width:'100%', padding:32, boxShadow:'0 40px 100px #00000090', textAlign:'center' }}>
        <div style={{ fontSize:44, marginBottom:12 }}>🔒</div>
        <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:22, fontWeight:700, color:C.cream, marginBottom:10 }}>
          {isFan ? t('upg.fan_title') : t('upg.prem_title')}
        </div>
        <div style={{ fontSize:13, color:C.muted, lineHeight:1.7, marginBottom:22 }}>
          {isFan ? t('upg.fan_desc') : t('upg.prem_desc')}
        </div>
        {isFan
          ? <Btn onClick={onClose}>{t('upg.understood')}</Btn>
          : <>
            <div style={{ display:'flex', gap:10, justifyContent:'center', marginBottom:12 }}>
              <Btn onClick={()=>startSubscription('monthly',profile.id).catch(e=>toast.error(e.message))}>{t('me.monthly')}</Btn>
              <Btn v='secondary' onClick={()=>startSubscription('annual',profile.id).catch(e=>toast.error(e.message))}>{t('me.annual')}</Btn>
            </div>
            <button onClick={onClose} style={{ background:'none', border:'none', color:C.dim, cursor:'pointer', fontSize:11 }}>{t('upg.not_now')}</button>
          </>
        }
      </div>
    </div>
  );
}

/* ════════════════════════════════════
   MAIN DASHBOARD
═══════════════════════════════════ */
export default function Dashboard() {
  const navigate = useNavigate();
  const { session, user, profile, setProfile, setProfiles, tab, setTab, userProfiles = [], switchProfile, lang, setLang } = useStore();
  const t = useT();
  const [profiles, setLocalProfiles] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [profileModal, setProfileModal] = useState(null);
  const [eventModal, setEventModal] = useState(null);
  const [chatPartner, setChatPartner] = useState(null);
  const [inviteTarget, setInviteTarget] = useState(null);
  const [messages, setMessages] = useState([]);
  const [calEntries, setCalEntries] = useState([]);
  const [loadingProfiles, setLoadingProfiles] = useState(true);
  const [showUpgrade, setShowUpgrade] = useState(null);
  const [publicEvents, setPublicEvents] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  const isFan = profile?.type === 'fan';
  const isAmateurArtist = profile?.type === 'artist' && (!profile?.artist_tier || profile?.artist_tier === 'amateur');
  const isPaidArtist = profile?.type === 'artist' && (profile?.artist_tier === 'local_legends' || profile?.artist_tier === 'all_stars');
  const isAllStars = profile?.type === 'artist' && profile?.artist_tier === 'all_stars';
  const isAmateurVenue = profile?.type === 'venue' && (!profile?.venue_type || profile?.venue_type === 'amateur');
  const isSubscribed = isPaidArtist || profile?.type === 'venue' || profile?.subscribed;

  const lockedFor = (tabKey) => {
    if (!['ai','cal','promo','inbox'].includes(tabKey)) return null;
    if (isFan) return 'fan';
    if (isAmateurArtist) return 'amateur';
    return null;
  };

  const searchRef = useRef(search);
  const filterRef = useRef(filter);
  searchRef.current = search;
  filterRef.current = filter;
  const isFirstMount = useRef(true);

  const loadProfiles = useCallback(async () => {
    if (filterRef.current === 'events') return;
    setLoadingProfiles(true);
    let data = null;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        data = await getAllProfiles({
          search: searchRef.current || undefined,
          type: filterRef.current !== 'all' ? filterRef.current : undefined,
        });
        break;
      } catch(e) {
        if (attempt === 2) toast.error('Erreur chargement: ' + e.message);
        else await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
      }
    }
    if (data) { setLocalProfiles(data); setProfiles(data); }
    setLoadingProfiles(false);
  }, []);

  useEffect(() => { loadProfiles(); }, [filter]);
  useEffect(() => {
    if (isFirstMount.current) { isFirstMount.current = false; return; }
    const t = setTimeout(loadProfiles, 350);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => { getPublicEvents().then(setPublicEvents).catch(()=>{}); }, []);

  const handleLogout = async () => {
    try { await signOut(); } catch(e) {}
    localStorage.clear();
    sessionStorage.clear();
    navigate('/auth');
  };
  const handleAddProfile = () => navigate('/onboard');
  const handleSwitchProfile = (profileId) => {
    switchProfile(profileId);
    toast.success(t('profile_changed'));
  };

  const openChat = (p) => { setChatPartner(p); setInviteTarget(null); };
  const openInvite = (p) => { setInviteTarget(p); setChatPartner(null); };

  const unread = messages.filter(m=>!m.read&&m.to_id===user?.id)?.length;

  const filtered = profiles.filter(a =>
    (filter === 'all' || a.type === filter) &&
    (!search || (a.name+a.genre+(a.region||'')+(a.country||'')).toLowerCase().includes(search.toLowerCase()))
  );
  const isAgent = profile?.type === 'booking_agent';
  const TABS = [
    { k:'map',    i:'🗺️',  l: t('nav.map') },
    { k:'list',   i:'📋',  l: t('nav.directory') },
    { k:'ai',     i:'🤖',  l: t('nav.ai') },
    { k:'inbox',  i:'💬',  l: t('nav.inbox'), badge: unread },
    { k:'cal',    i:'📅',  l: t('nav.calendar') },
    { k:'promo',  i:'📣',  l: t('nav.promo') },
    isAgent ? { k:'agency', i:'🎫', l:'Agence' } : null,
    { k:'me',     i:'👤',  l: t('nav.profile') },
  ].filter(Boolean).map(tab => ({ ...tab, locked: lockedFor(tab.k) }));

  return (
    <div style={{fontFamily:"'Outfit',sans-serif",background:C.bg,color:C.text,minHeight:'100vh'}}>
      <header style={{background:'linear-gradient(135deg,'+C.bg+' 0%,'+C.card+' 100%)',borderBottom:'1px solid '+C.border,padding:'0 20px',height:58,display:'flex',alignItems:'center',gap:10,position:'sticky',top:0,zIndex:500,boxShadow:'0 4px 32px #00000060',overflow:'visible'}}>
        <div style={{display:'flex',alignItems:'center'}}>
          <img src="/logo.svg" alt="StageMap" style={{height:44}}/>
        </div>

        <nav style={{display:isMobile?'none':'flex',gap:1,marginLeft:12,flex:1,overflowX:'auto'}}>
          {TABS.map(n=>(
            <button key={n.k} onClick={()=>{ if(n.locked) setShowUpgrade(n.locked); else setTab(n.k); }}
              style={{position:'relative',background:tab===n.k?C.orange+'20':'none',border:'1px solid '+(tab===n.k?C.orange+'66':'transparent'),color:n.locked?C.dim:tab===n.k?C.orangeLt:C.muted,borderRadius:7,padding:'4px 11px',cursor:'pointer',fontSize:11,fontWeight:tab===n.k?600:400,fontFamily:"'Outfit',sans-serif",transition:'all .15s',whiteSpace:'nowrap',opacity:n.locked?.6:1}}>
              {n.i} {n.l}{n.locked?' 🔒':''}
              {n.badge>0&&<span style={{position:'absolute',top:0,right:0,background:C.red,color:'#fff',borderRadius:'50%',width:14,height:14,fontSize:9,display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700}}>{n.badge}</span>}
            </button>
          ))}
        </nav>

        <button onClick={() => setLang(lang === 'fr' ? 'en' : 'fr')}
          style={{ background:C.tag, border:'1px solid '+C.border, borderRadius:20, padding:'4px 10px', fontSize:11, color:C.muted, cursor:'pointer', fontFamily:"'Outfit',sans-serif", fontWeight:600, flexShrink:0 }}>
          {lang === 'fr' ? '🇬🇧 EN' : '🇫🇷 FR'}
        </button>
        {profile && (
          <ProfileSwitcher
            profile={profile}
            userProfiles={userProfiles && userProfiles?.length > 0 ? userProfiles : [profile]}
            onSwitch={handleSwitchProfile}
            onAdd={handleAddProfile}
            onLogout={handleLogout}
          />
        )}
      </header>

      {(tab==='map'||tab==='list')&&(
        <div style={{background:C.card,borderBottom:'1px solid '+C.border,padding:'8px 20px',display:'flex',gap:7,flexWrap:'wrap',alignItems:'center'}}>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder={t('header.search_ph')} style={{background:C.tag,border:'1px solid '+C.border,borderRadius:8,padding:'6px 12px',color:C.text,fontFamily:"'Outfit',sans-serif",fontSize:12,outline:'none',width:220}}/>
          {['all','artist','venue','fan'].map(f=>(
            <button key={f} onClick={()=>setFilter(f)} style={{background:filter===f?(typeColors[f]||C.orange)+'22':C.tag,color:filter===f?(typeColors[f]||C.orange):C.muted,border:'1px solid '+(filter===f?(typeColors[f]||C.orange):C.border),borderRadius:20,padding:'3px 11px',cursor:'pointer',fontSize:11,fontWeight:filter===f?600:400,fontFamily:"'Outfit',sans-serif"}}>
              {f==='all'?t('filter.all'):(f==='artist'?'🎵 ':f==='venue'?'🏛️ ':'💛 ')+t('type.'+f)}
            </button>
          ))}
          <button onClick={()=>setFilter('events')} style={{background:filter==='events'?C.purple+'22':C.tag,color:filter==='events'?C.purple:C.muted,border:'1px solid '+(filter==='events'?C.purple:C.border),borderRadius:20,padding:'3px 11px',cursor:'pointer',fontSize:11,fontWeight:filter==='events'?600:400,fontFamily:"'Outfit',sans-serif"}}>
            {t('filter.events')}
          </button>
          <span style={{marginLeft:'auto',color:C.dim,fontSize:11}}>
            {filter==='events'
              ? t('events_filter_count', {n: publicEvents.length, s: publicEvents.length!==1?'s':''})
              : loadingProfiles ? t('loading') : t('profiles_n', {n: filtered?.length})}
          </span>
        </div>
      )}

      <main style={{padding: isMobile ? '14px' : '20px', maxWidth:1100, margin:'0 auto', paddingBottom: isMobile ? 88 : 20}}>
        {tab==='map'&&<MapView artists={filter==='events'?[]:filtered} myProfile={profile} onOpen={setProfileModal} onOpenEvent={setEventModal} events={filter==='events'?publicEvents:[]} allEvents={publicEvents}/>}
        {tab==='list'&&filter!=='events'&&(
          <FeaturedCarousel
            profiles={profiles.filter(p=>p.subscribed)}
            events={publicEvents}
            onOpenProfile={setProfileModal}
            onOpenEvent={setEventModal}
          />
        )}
        {tab==='list'&&filter!=='events'&&(
          <div className='fade-in' style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))',gap:12}}>
            {loadingProfiles&&profiles.length===0&&<div style={{gridColumn:'1/-1',textAlign:'center',padding:'40px 0',display:'flex',justifyContent:'center'}}><Spinner/></div>}
            {filtered.map(a=><ProfileCard key={a.id} a={a} myId={profile?.id} onOpen={setProfileModal}/>)}
            {!loadingProfiles&&filtered?.length===0&&<div style={{gridColumn:'1/-1',color:C.dim,textAlign:'center',padding:'30px 0'}}>{t('no_profiles')}</div>}
          </div>
        )}
        {tab==='list'&&filter==='events'&&(
          <div className='fade-in'>
            {publicEvents.length===0&&<div style={{color:C.dim,textAlign:'center',padding:'40px 0',fontSize:13}}>{t('events_public_empty')}</div>}
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))',gap:10}}>
              {publicEvents.map(ev=>{
                const p=ev.profile;
                const ec=ev.event_type==='booking'?C.amber:ev.event_type==='event'?C.orange:C.muted;
                return (
                  <div key={ev.id} onClick={()=>setEventModal(ev)}
                    style={{background:C.card,border:'1px solid '+C.purple+'44',borderLeft:'3px solid '+C.purple,borderRadius:9,padding:'12px 14px',cursor:'pointer',transition:'all .15s'}}
                    onMouseEnter={e=>e.currentTarget.style.background=C.cardHov}
                    onMouseLeave={e=>e.currentTarget.style.background=C.card}>
                    <div style={{display:'flex',alignItems:'center',gap:9,marginBottom:7}}>
                      <span style={{fontSize:26}}>{p?.avatar||'📅'}</span>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontWeight:700,fontSize:13,color:C.text,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{ev.title}</div>
                        <div style={{fontSize:11,color:C.muted}}>{p?.name} · {p?.region}</div>
                      </div>
                      <span style={{background:C.purple+'22',color:C.purple,border:'1px solid '+C.purple+'44',borderRadius:20,padding:'1px 8px',fontSize:9,fontWeight:700,whiteSpace:'nowrap'}}>🌐 Public</span>
                    </div>
                    <div style={{display:'flex',gap:10,alignItems:'center'}}>
                      <span style={{fontSize:11,color:C.amber}}>📅 {new Date(ev.date_start).toLocaleDateString('fr',{weekday:'long',day:'numeric',month:'long'})}</span>
                    </div>
                    {ev.description&&<div style={{fontSize:11,color:C.dim,marginTop:5,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{ev.description}</div>}
                    <div style={{display:'flex',gap:6,marginTop:6}}>
                      <span style={{background:ec+'22',color:ec,borderRadius:20,padding:'1px 7px',fontSize:10}}>{p?.type==='venue'?'🏛️ Lieu':'🎵 Artiste'}</span>
                      {p?.genre&&<span style={{fontSize:10,color:C.dim}}>· {p.genre}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {tab==='ai'&&<AIPanel profiles={profiles} myProfile={profile} onLaunchTour={async (plan) => {
          try {
            for (const stop of plan.stops) {
              await createInvitation({ organizer_id: profile.id, invitee_id: stop.profileId, tour_title: plan.title, date: stop.date, city: stop.city, role: stop.role, note: stop.note, status: 'pending' });
            }
            toast.success(`${plan.stops.length} invitation(s) envoyée(s) !`);
          } catch(e) { toast.error('Erreur : '+e.message); }
        }}/>}
        {tab==='inbox'&&<InboxView messages={messages} myId={user?.id} profiles={profiles} onChat={openChat} onRefresh={()=>getMessages(user.id).then(setMessages)}/>}
        {tab==='cal'&&<CalendarView myId={profile?.id} profiles={profiles} myProfile={profile} onInvite={(p, date) => { setInviteTarget(p); }} />}
        {tab==='promo'&&<PromoModule myProfile={profile} isSubscribed={isSubscribed}/>}
        {tab==='agency'&&profile&&<AgencyTab profile={profile}/>}
        {tab==='me'&&profile&&<MyProfileTab profile={profile} userProfiles={userProfiles||[profile]} setProfile={setProfile} user={user} onLogout={handleLogout} onAddProfile={handleAddProfile} isSubscribed={isSubscribed}/>}
      </main>

      <footer style={{borderTop:'1px solid '+C.border,padding:'12px 20px',display:'flex',justifyContent:'space-between',color:C.dim,fontSize:11}}>
        <span>{t('footer.copy')}</span>
        <span>{t('footer.count', {n: profiles?.length})}</span>
      </footer>

      {/* ── Mobile bottom nav ── */}
      {isMobile && (
        <div style={{ position:'fixed', bottom:0, left:0, right:0, height:64, background:C.bg, borderTop:'1px solid '+C.border, display:'flex', alignItems:'center', zIndex:600, boxShadow:'0 -4px 24px #00000080' }}>
          {[
            { k:'map',   i:'🗺️', l:'Carte' },
            { k:'list',  i:'📋', l:'Annuaire' },
            null,
            { k:'inbox', i:'💬', l:'Messages', badge: unread },
            { k:'me',    i:'👤', l:'Profil' },
          ].map((item, idx) => {
            if (!item) return (
              <div key='fab' style={{ flex:1, display:'flex', justifyContent:'center' }}>
                <button
                  onClick={() => setTab('ai')}
                  style={{ width:52, height:52, borderRadius:'50%', background:'linear-gradient(135deg,'+C.orange+','+C.orangeLt+')', border:'3px solid '+C.bg, boxShadow:'0 0 20px '+C.orange+'55', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, cursor:'pointer', marginBottom:16, flexShrink:0 }}>
                  🤖
                </button>
              </div>
            );
            const active = tab === item.k;
            return (
              <button key={item.k} onClick={() => setTab(item.k)}
                style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:2, background:'none', border:'none', cursor:'pointer', position:'relative', paddingTop:4 }}>
                {item.badge > 0 && (
                  <div style={{ position:'absolute', top:2, right:'calc(50% - 14px)', background:C.red, color:'#fff', borderRadius:'50%', width:14, height:14, fontSize:8, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700 }}>{item.badge}</div>
                )}
                <span style={{ fontSize:18 }}>{item.i}</span>
                <span style={{ fontSize:9, color:active?C.orange:C.muted, fontFamily:"'Outfit',sans-serif", fontWeight:active?700:400, letterSpacing:.3 }}>{item.l}</span>
                {active && <div style={{ position:'absolute', bottom:0, left:'50%', transform:'translateX(-50%)', width:24, height:2, background:C.orange, borderRadius:1 }} />}
              </button>
            );
          })}
        </div>
      )}

      {profileModal&&<ProfileModal a={profileModal} myId={profile?.id} myProfile={profile} onClose={()=>setProfileModal(null)} onChat={openChat} onInvite={openInvite}/>}
      {eventModal&&<EventModal ev={eventModal} onClose={()=>setEventModal(null)}/>}
      {chatPartner&&profile&&<ChatPanel myProfile={profile} partner={chatPartner} onClose={()=>setChatPartner(null)}/>}
      {inviteTarget&&profile&&(
        <InviteModal organizer={profile} invitee={inviteTarget} profiles={profiles} onClose={()=>setInviteTarget(null)} onSent={()=>{setInviteTarget(null);getMessages(user.id).then(setMessages);}}/>
      )}
      {showUpgrade&&<UpgradeModal reason={showUpgrade} profile={profile} onClose={()=>setShowUpgrade(null)}/>}
    </div>
  );
}
